import React, { useState, useEffect, useRef } from 'react';

// Smart month detection utility for sorting
const getMonthSortValue = (name) => {
  if (!name || typeof name !== 'string') return name;
  
  const monthMap = {
    'january': '01', 'jan': '01',
    'february': '02', 'feb': '02',
    'march': '03', 'mar': '03',
    'april': '04', 'apr': '04',
    'may': '05',
    'june': '06', 'jun': '06',
    'july': '07', 'jul': '07',
    'august': '08', 'aug': '08',
    'september': '09', 'sep': '09', 'sept': '09',
    'october': '10', 'oct': '10',
    'november': '11', 'nov': '11',
    'december': '12', 'dec': '12'
  };
  
  const lowerName = name.toLowerCase();
  
  // Check if the name contains any month name/abbreviation
  for (const [monthName, monthValue] of Object.entries(monthMap)) {
    if (lowerName.includes(monthName)) {
      // Replace the month name with the month number for sorting
      // This creates a sort key while preserving the original display name
      return lowerName.replace(monthName, monthValue);
    }
  }
  
  // No month detected, return original name for regular alphabetical sorting
  return lowerName;
};

const FileManager = ({ user, onBack }) => {
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('name'); // 'name', 'date', 'size'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetItem, setTargetItem] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [copiedFolder, setCopiedFolder] = useState(null);
  const [showPasteConfirmModal, setShowPasteConfirmModal] = useState(false);

  const contextMenuRef = useRef(null);
  const fileInputRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    loadFolderContents(null);
  }, []);

  // Handle click outside to clear selection
  useEffect(() => {
    const handleContentClick = (event) => {
      // Check if click is on the content area background (not on items)
      if (contentRef.current && event.target === contentRef.current) {
        setSelectedItems([]);
      }
    };

    // Handle Escape key to clear selection
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedItems([]);
        setContextMenu(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        setContextMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadFolderContents = async (folderId) => {
    setLoading(true);
    try {
      // Fetch all folders to build structure
      const foldersResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (foldersResponse.ok) {
        const allFolders = await foldersResponse.json();

        // Filter folders for current directory
        const currentFolders = allFolders.filter(f => f.parent_id === folderId);
        setFolders(currentFolders);

        // Build breadcrumb
        if (folderId) {
          const path = [];
          let current = allFolders.find(f => f.id === folderId);
          while (current) {
            path.unshift(current);
            current = allFolders.find(f => f.id === current.parent_id);
          }
          setBreadcrumb(path);
        } else {
          setBreadcrumb([]);
        }
      }

      // Fetch documents for current folder
      if (folderId) {
        // Ensure folderId is a number for the API call
        const folderIdNum = parseInt(folderId);

        // Try the regular folders endpoint as a fallback
        const docsResponse = await fetch(`${window.rwcvaultData.apiUrl}/folders/${folderIdNum}/documents`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
          }
        });


        if (docsResponse.ok) {
          const docs = await docsResponse.json();
          setDocuments(docs);
        } else {
          console.error('Failed to fetch documents:', docsResponse.status, docsResponse.statusText);
          // If regular endpoint fails, try to get all documents and filter
          try {
            const allDocsResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/documents`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
              }
            });

            if (allDocsResponse.ok) {
              const allDocs = await allDocsResponse.json();
                // Filter documents for current folder
              const folderDocs = allDocs.filter(doc => doc.folder_id === folderIdNum);
              setDocuments(folderDocs);
            } else {
              setDocuments([]);
            }
          } catch (err) {
            console.error('Error fetching all documents:', err);
            setDocuments([]);
          }
        }
      } else {
        // Root level - no documents
        setDocuments([]);
      }

      setCurrentFolder(folderId);
      setSelectedItems([]);
    } catch (err) {
      console.error('Error loading folder contents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderDropFromFiles = async (files) => {
    
    const filesWithPaths = [];
    const allFolderPaths = new Set();
    
    // Check if we're dealing with empty folders by looking at file names
    const hasEmptyFolders = files.some(file => {
      // Empty folders sometimes appear as files with no extension and specific names
      return !file.name.includes('.') && file.size === 0;
    });
    
    files.forEach((file, index) => {
      
      if (file.webkitRelativePath && file.webkitRelativePath.length > 0) {
        // This file is from a folder structure
        filesWithPaths.push({
          file: file,
          relativePath: file.webkitRelativePath
        });
        
        // Extract folder paths from the file path
        const pathParts = file.webkitRelativePath.split('/');
        // Remove filename to get folder path
        pathParts.pop();
        
        // Add all nested folder paths
        let currentPath = '';
        pathParts.forEach(part => {
          currentPath += (currentPath ? '/' : '') + part;
          allFolderPaths.add(currentPath);
        });
      } else if (hasEmptyFolders && !file.name.includes('.') && file.size === 0) {
        // This might be an empty folder
        allFolderPaths.add(file.name);
      } else {
        // Individual file (not from folder)
        filesWithPaths.push({
          file: file,
          relativePath: file.name
        });
      }
    });
    
    
    // If we detected empty folders, create them
    if (allFolderPaths.size > 0 && filesWithPaths.length === 0) {
      try {
        showToast(`Creating ${allFolderPaths.size} empty folders...`, 'info');
        
        const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/create-folders`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            folder_id: currentFolder,
            folder_paths: Array.from(allFolderPaths)
          })
        });

        if (response.ok) {
          const result = await response.json();
          showToast(`Created ${result.created_folders.length} empty folders`);
          await loadFolderContents(currentFolder);
        } else {
          const errorData = await response.json();
          console.error('Folder creation failed:', errorData);
          showToast('Failed to create folders: ' + (errorData.message || 'Unknown error'), 'error');
        }
      } catch (err) {
        console.error('Error creating empty folders:', err);
        showToast('Error creating folders', 'error');
      }
      return;
    }
    
    // Check if we have any files to process
    if (filesWithPaths.length > 0) {
      // Filter for allowed file types
      const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
      const validFilesWithPaths = filesWithPaths.filter(item => {
        const extension = item.file.name.split('.').pop().toLowerCase();
        return allowedTypes.includes(extension);
      });

      if (validFilesWithPaths.length === 0) {
        showToast('No valid files found in dropped folders. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV', 'error');
        return;
      }

      if (validFilesWithPaths.length < filesWithPaths.length) {
        showToast(`Only ${validFilesWithPaths.length} of ${filesWithPaths.length} files are valid and will be uploaded`, 'info');
      }

      // Show progress modal for folder upload with all detected folder paths
      showProgressModalWithPathsAndFolders(validFilesWithPaths, allFolderPaths);
    } else if (allFolderPaths.size === 0) {
      showToast('No files or folders found in dropped items', 'error');
    }
  };

  const navigateToFolder = (folderId) => {
    loadFolderContents(folderId);
  };

  const handleContextMenu = (event, item = null) => {
    event.preventDefault();
    event.stopPropagation();

    const menuType = item ? item.type : 'empty';
    const menuItems = getContextMenuItems(menuType, item);

    // Calculate position to avoid going off-screen
    let x = event.clientX;
    let y = event.clientY;
    
    // Estimate menu dimensions (can be adjusted based on your actual menu)
    const menuWidth = 200;
    const menuHeight = menuItems.length * 40 + 20; // Rough estimate
    
    // Check if menu would go off right edge
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 10;
    }
    
    // Check if menu would go off bottom edge
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 10;
    }

    setContextMenu({
      x: x,
      y: y,
      type: menuType,
      item: item,
      items: menuItems
    });
  };

  const getContextMenuItems = (type, item) => {
    const items = [];

    if (type === 'folder') {
      items.push(
        { label: 'Open', icon: '📂', action: () => navigateToFolder(item.id) },
        { divider: true },
        { label: 'Rename', icon: '✏️', action: () => handleRename(item) },
        { label: 'Move', icon: '➡️', action: () => handleMove(item) },
        { label: 'Copy Folders Only', icon: '📋', action: () => handleCopyFoldersOnly(item) },
        { divider: true }
      );

      // Only show Edit Permissions for root folders (parent_id is null)
      if (item.parent_id === null) {
        items.push(
          { label: 'Edit Permissions', icon: '🔒', action: () => handleEditPermissions(item) }
        );
      }

      items.push(
        { label: 'Download as ZIP', icon: '📦', action: () => handleDownloadZip(item) },
        { divider: true },
        { label: 'Delete', icon: '🗑️', action: () => handleDelete(item), danger: true }
      );
    } else if (type === 'document') {
      items.push(
        { label: 'Download', icon: '⬇️', action: () => handleDownload(item) },
        { divider: true },
        { label: 'Rename', icon: '✏️', action: () => handleRename(item) },
        { label: 'Move', icon: '➡️', action: () => handleMove(item) },
        { divider: true },
        { label: 'Delete', icon: '🗑️', action: () => handleDelete(item), danger: true }
      );
    } else if (type === 'empty') {
      // Add clear selection option if items are selected
      if (selectedItems.length > 0) {
        items.push(
          { label: 'Clear Selection', icon: '❌', action: () => setSelectedItems([]) },
          { divider: true }
        );
      }

      // Add paste option if folder is copied
      if (copiedFolder) {
        items.push(
          { label: 'Paste Folders Only', icon: '📋', action: () => setShowPasteConfirmModal(true) },
          { divider: true }
        );
      }

      // Only show upload option if we're in a folder (not root)
      if (currentFolder) {
        items.push(
          { label: 'New Folder', icon: '📁', action: () => handleNewFolder() },
          { label: 'Upload Files', icon: '📤', action: () => handleUploadFiles() },
          { label: 'Upload Folder', icon: '📂', action: () => handleUploadFolder() }
        );
      } else {
        items.push(
          { label: 'New Folder', icon: '📁', action: () => handleNewFolder() }
        );
      }
    }

    // Add multi-select options if multiple items selected
    if (selectedItems.length > 1) {
      const multiItems = [
        { label: `Move ${selectedItems.length} items`, icon: '➡️', action: () => handleMoveMultiple() },
        { divider: true },
        { label: `Delete ${selectedItems.length} items`, icon: '🗑️', action: () => handleDeleteMultiple(), danger: true }
      ];

      // Only add download option if all selected items are documents
      const allDocuments = selectedItems.every(key => key.startsWith('document-'));
      if (allDocuments) {
        multiItems.unshift(
          { label: `Download ${selectedItems.length} files`, icon: '⬇️', action: () => handleDownloadMultiple() },
          { divider: true }
        );
      }

      return multiItems;
    }

    return items;
  };

  const handleNewFolder = () => {
    setContextMenu(null);
    setShowNewFolderModal(true);
  };

  const handleUploadFiles = () => {
    setContextMenu(null);
    // Trigger the file input
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv';
    input.webkitdirectory = false; // For individual files
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        await uploadFiles(files);
      }
    };
    input.click();
  };

  const handleUploadFolder = () => {
    setContextMenu(null);
    // Trigger the folder input
    const input = document.createElement('input');
    input.type = 'file';
    input.webkitdirectory = true;
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        await uploadFilesWithFolders(files);
      }
    };
    input.click();
  };

  const handleCopyFoldersOnly = (item) => {
    setContextMenu(null);
    setCopiedFolder({
      id: item.id,
      name: item.name
    });
    showToast(`Copied "${item.name}" folder structure to clipboard`);
  };

  const handlePasteFoldersOnly = async (newName = null, skipRoot = false) => {
    if (!copiedFolder) {
      showToast('No folder structure copied', 'error');
      return;
    }

    setShowPasteConfirmModal(false);

    const folderName = skipRoot ? copiedFolder.name : (newName || copiedFolder.name);

    try {
      showToast(skipRoot
        ? `Pasting sub-folders from "${folderName}"...`
        : `Pasting "${folderName}" folder structure...`, 'info');

      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders/copy-structure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_folder_id: copiedFolder.id,
          destination_parent_id: currentFolder,
          new_name: newName,
          skip_root: skipRoot
        })
      });

      if (response.ok) {
        const result = await response.json();
        showToast(`Successfully pasted folder structure (${result.created_count} folders created)`);

        // Refresh the current folder
        await loadFolderContents(currentFolder);

        // Broadcast update
        const channel = new BroadcastChannel('rwcvault_updates');
        channel.postMessage({ type: 'folders_updated' });
        channel.close();
      } else {
        const error = await response.json();
        showToast(error.message || 'Failed to paste folder structure', 'error');
      }
    } catch (err) {
      console.error('Error pasting folder structure:', err);
      showToast('Failed to paste folder structure', 'error');
    }
  };

  const uploadFiles = async (files) => {
    if (!currentFolder) {
      showToast('Please select a folder first', 'error');
      return;
    }

    showToast(`Uploading ${files.length} file(s)...`, 'info');

    // Filter allowed file types
    const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(extension);
    });

    if (validFiles.length === 0) {
      showToast('No valid files to upload. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV', 'error');
      return;
    }

    if (validFiles.length < files.length) {
      showToast(`Only ${validFiles.length} of ${files.length} files are valid and will be uploaded`, 'info');
    }

    const formData = new FormData();
    formData.append('folder_id', currentFolder);
    validFiles.forEach(file => {
      formData.append('documents[]', file);
    });

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        },
        body: formData
      });


      if (response.ok) {
        try {
          const result = await response.json();
          const { uploaded, errors } = result;

        if (uploaded.length > 0) {
          showToast(`Successfully uploaded ${uploaded.length} file(s)`);
        }
        
        if (errors.length > 0) {
          // Create detailed error objects with file references for retry
          const detailedErrors = errors.map(error => {
            const originalFile = validFiles.find(f => f.name === error.file);
            return {
              file: originalFile || { name: error.file },
              fileName: error.file,
              error: error.error,
              timestamp: new Date(),
              folderId: currentFolder
            };
          });
          
          // Add to error tracking
          setUploadErrors(prev => [...prev, ...detailedErrors]);
          
          // Show summary error message
          showToast(`${errors.length} file(s) failed to upload. Click "View Errors" to see details and retry.`, 'error');
          
          // Show error report automatically if there are errors
          setShowErrorReport(true);
        }

        // Refresh the current folder
        await loadFolderContents(currentFolder);

        // Broadcast update
        const channel = new BroadcastChannel('rwcvault_updates');
        channel.postMessage({ type: 'documents_updated' });
        channel.close();
        } catch (jsonErr) {
          // Handle JSON parsing errors for successful responses
          console.error('JSON parsing error on successful response:', jsonErr);
          const allFileErrors = validFiles.map(file => ({
            file: file,
            fileName: file.name,
            error: 'Server response error: ' + jsonErr.message,
            timestamp: new Date(),
            folderId: currentFolder
          }));
          
          setUploadErrors(prev => [...prev, ...allFileErrors]);
          setShowErrorReport(true);
          showToast('Upload response error', 'error');
        }
      } else {
        try {
          const data = await response.json();
        console.error('Upload failed:', data);
        
        // Create error objects for complete upload failure
        const allFileErrors = validFiles.map(file => ({
          file: file,
          fileName: file.name,
          error: data.message || 'Upload failed',
          timestamp: new Date(),
          folderId: currentFolder
        }));
        
        setUploadErrors(prev => [...prev, ...allFileErrors]);
        setShowErrorReport(true);
        showToast(data.message || 'Upload failed', 'error');
        } catch (jsonErr) {
          // Handle JSON parsing errors for error responses
          console.error('JSON parsing error on error response:', jsonErr);
          const errorMessage = `Server error (${response.status}): ${response.statusText || 'Unable to process request'}`;
          const allFileErrors = validFiles.map(file => ({
            file: file,
            fileName: file.name,
            error: errorMessage,
            timestamp: new Date(),
            folderId: currentFolder
          }));
          
          setUploadErrors(prev => [...prev, ...allFileErrors]);
          setShowErrorReport(true);
          showToast(errorMessage, 'error');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
      
      // Create error objects for connection failure
      const connectionErrors = validFiles.map(file => ({
        file: file,
        fileName: file.name,
        error: 'Connection error during upload',
        timestamp: new Date(),
        folderId: currentFolder
      }));
      
      setUploadErrors(prev => [...prev, ...connectionErrors]);
      setShowErrorReport(true);
      showToast('Connection error during upload', 'error');
    }
  };

  // Retry failed uploads
  const retryFailedUploads = async (errorItems) => {
    if (!errorItems || errorItems.length === 0) return;
    
    showToast(`Retrying ${errorItems.length} file(s)...`, 'info');
    
    // Group by folder ID to handle different target folders
    const groupedByFolder = errorItems.reduce((groups, item) => {
      const folderId = item.folderId;
      if (!groups[folderId]) groups[folderId] = [];
      groups[folderId].push(item);
      return groups;
    }, {});
    
    // Remove the items we're retrying from the error list
    setUploadErrors(prev => prev.filter(error => 
      !errorItems.some(retry => retry.fileName === error.fileName && retry.timestamp === error.timestamp)
    ));
    
    // Process each folder group
    for (const [folderId, items] of Object.entries(groupedByFolder)) {
      const files = items.map(item => item.file).filter(file => file);
      if (files.length > 0) {
        await uploadFilesToFolder(files, folderId);
      }
    }
  };
  
  // Helper function to upload files to a specific folder
  const uploadFilesToFolder = async (files, folderId) => {
    const formData = new FormData();
    formData.append('folder_id', folderId);
    files.forEach(file => {
      formData.append('documents[]', file);
    });

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        },
        body: formData
      });

      if (response.ok) {
        try {
          const result = await response.json();
          const { uploaded, errors } = result;

        if (uploaded.length > 0) {
          showToast(`Successfully uploaded ${uploaded.length} file(s) on retry`);
        }
        
        if (errors.length > 0) {
          // Add failed retries back to error tracking
          const detailedErrors = errors.map(error => {
            const originalFile = files.find(f => f.name === error.file);
            return {
              file: originalFile || { name: error.file },
              fileName: error.file,
              error: error.error,
              timestamp: new Date(),
              folderId: folderId
            };
          });
          
          setUploadErrors(prev => [...prev, ...detailedErrors]);
          showToast(`${errors.length} file(s) still failed on retry`, 'error');
        }

        // Refresh if we're viewing the same folder
        if (currentFolder === folderId) {
          await loadFolderContents(currentFolder);
        }
        } catch (jsonErr) {
          // Handle JSON parsing errors for successful responses
          console.error('JSON parsing error on retry success response:', jsonErr);
          const allFileErrors = files.map(file => ({
            file: file,
            fileName: file.name,
            error: 'Retry response error: ' + jsonErr.message,
            timestamp: new Date(),
            folderId: folderId
          }));
          
          setUploadErrors(prev => [...prev, ...allFileErrors]);
          showToast('Retry response error', 'error');
        }
        
      } else {
        try {
          const data = await response.json();
        // Add back to errors
        const allFileErrors = files.map(file => ({
          file: file,
          fileName: file.name,
          error: data.message || 'Retry failed',
          timestamp: new Date(),
          folderId: folderId
        }));
        
        setUploadErrors(prev => [...prev, ...allFileErrors]);
        showToast(`Retry failed: ${data.message || 'Unknown error'}`, 'error');
        } catch (jsonErr) {
          // Handle JSON parsing errors for error responses
          console.error('JSON parsing error on retry error response:', jsonErr);
          const errorMessage = `Retry server error (${response.status}): ${response.statusText || 'Unable to process request'}`;
          const allFileErrors = files.map(file => ({
            file: file,
            fileName: file.name,
            error: errorMessage,
            timestamp: new Date(),
            folderId: folderId
          }));
          
          setUploadErrors(prev => [...prev, ...allFileErrors]);
          showToast(errorMessage, 'error');
        }
      }
    } catch (err) {
      // Add back to errors
      const connectionErrors = files.map(file => ({
        file: file,
        fileName: file.name,
        error: 'Connection error during retry: ' + err.message,
        timestamp: new Date(),
        folderId: folderId
      }));
      
      setUploadErrors(prev => [...prev, ...connectionErrors]);
      showToast('Connection error during retry: ' + err.message, 'error');
    }
  };

  const uploadFilesWithFolders = async (files) => {
    if (!currentFolder) {
      showToast('Please select a folder first', 'error');
      return;
    }

    
    // Filter allowed file types
    const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
    const validFiles = files.filter(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(extension);
    });

    if (validFiles.length === 0) {
      showToast('No valid files to upload. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV', 'error');
      return;
    }

    if (validFiles.length < files.length) {
      showToast(`Only ${validFiles.length} of ${files.length} files are valid and will be uploaded`, 'info');
    }

    // Convert files to filesWithPaths format for compatibility
    const filesWithPaths = validFiles.map(file => ({
      file: file,
      relativePath: file.webkitRelativePath || file.name
    }));

    // Show progress modal for folder upload
    showProgressModalWithPaths(filesWithPaths);
  };

  const showProgressModalWithPaths = (filesWithPaths) => {
    // Create progress modal
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 24px;
      width: 500px;
      max-width: 90%;
    `;

    const title = document.createElement('h3');
    title.textContent = 'Uploading Folder Structure';
    title.style.marginBottom = '16px';

    const progressBar = document.createElement('div');
    progressBar.style.cssText = `
      width: 100%;
      height: 20px;
      background: #f0f0f0;
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 16px;
    `;

    const progressFill = document.createElement('div');
    progressFill.style.cssText = `
      height: 100%;
      background: #4f46e5;
      width: 0%;
      transition: width 0.3s ease;
    `;
    progressBar.appendChild(progressFill);

    const statusText = document.createElement('div');
    statusText.style.cssText = `
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
      font-weight: 500;
    `;
    statusText.textContent = 'Preparing folder upload...';

    const detailsText = document.createElement('div');
    detailsText.style.cssText = `
      font-size: 12px;
      color: #888;
      margin-bottom: 12px;
      min-height: 18px;
    `;
    detailsText.textContent = `Total files: ${filesWithPaths.length}`;

    const speedText = document.createElement('div');
    speedText.style.cssText = `
      font-size: 12px;
      color: #666;
      margin-bottom: 16px;
      text-align: right;
      min-height: 18px;
    `;

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.cssText = `
      background: #e5e7eb;
      color: #374151;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
    `;

    let uploadCancelled = false;
    cancelButton.onclick = () => {
      uploadCancelled = true;
      document.body.removeChild(modal);
    };

    content.appendChild(title);
    content.appendChild(progressBar);
    content.appendChild(statusText);
    content.appendChild(detailsText);
    content.appendChild(speedText);
    content.appendChild(cancelButton);
    modal.appendChild(content);
    document.body.appendChild(modal);

    // Start the upload process with paths
    processBatchUploadWithPaths(filesWithPaths, progressFill, statusText, detailsText, speedText, modal, () => uploadCancelled);
  };

  const showProgressModal = (files) => {
    // Create progress modal
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 3000;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 24px;
      width: 500px;
      max-width: 90%;
    `;

    const title = document.createElement('h3');
    title.textContent = 'Uploading Files';
    title.style.marginBottom = '16px';

    const progressBar = document.createElement('div');
    progressBar.style.cssText = `
      width: 100%;
      height: 20px;
      background: #f0f0f0;
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 16px;
    `;

    const progressFill = document.createElement('div');
    progressFill.style.cssText = `
      height: 100%;
      background: #4f46e5;
      width: 0%;
      transition: width 0.3s ease;
    `;
    progressBar.appendChild(progressFill);

    const statusText = document.createElement('div');
    statusText.style.cssText = `
      font-size: 14px;
      color: #666;
      margin-bottom: 8px;
      font-weight: 500;
    `;
    statusText.textContent = 'Preparing upload...';

    const detailsText = document.createElement('div');
    detailsText.style.cssText = `
      font-size: 12px;
      color: #888;
      margin-bottom: 12px;
      min-height: 18px;
    `;
    detailsText.textContent = `Total files: ${files.length}`;

    const speedText = document.createElement('div');
    speedText.style.cssText = `
      font-size: 12px;
      color: #666;
      margin-bottom: 16px;
      text-align: right;
      min-height: 18px;
    `;

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.cssText = `
      background: #e5e7eb;
      color: #374151;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
    `;

    let uploadCancelled = false;
    cancelButton.onclick = () => {
      uploadCancelled = true;
      document.body.removeChild(modal);
    };

    content.appendChild(title);
    content.appendChild(progressBar);
    content.appendChild(statusText);
    content.appendChild(detailsText);
    content.appendChild(speedText);
    content.appendChild(cancelButton);
    modal.appendChild(content);
    document.body.appendChild(modal);

    // Start the upload process
    processBatchUpload(files, progressFill, statusText, detailsText, speedText, modal, () => uploadCancelled);
  };

  const processBatchUploadWithPaths = async (filesWithPaths, progressFill, statusText, detailsText, speedText, modal, isCancelled) => {
    // Optimized batch size for better performance while maintaining stability
    const maxBatchSize = 25; // Increased from 10 based on server capacity analysis
    const totalFiles = filesWithPaths.length;
    let processed = 0;
    let errors = [];
    let successful = 0;
    let startTime = Date.now();
    let totalBytes = 0;

    try {
    // Get all folder paths that were detected during the drop (including empty ones)
    // This should be passed from the handleFolderDrop function
    const allDetectedPaths = window.currentDropFolderPaths || new Set();
    
    // Also extract folder paths from files
    const fileFolderPaths = new Set();
    filesWithPaths.forEach(item => {
    if (item.relativePath) {
    const pathParts = item.relativePath.split('/');
    // Remove filename to get folder path
    pathParts.pop();
    
    // Add all nested paths
    let currentPath = '';
    pathParts.forEach(part => {
    currentPath += (currentPath ? '/' : '') + part;
      fileFolderPaths.add(currentPath);
      });
      }
    });
    
    // Combine both sets to get all folders
    const allPaths = new Set([...allDetectedPaths, ...fileFolderPaths]);
    

    // First, create all folders (including empty ones)
    if (allPaths.size > 0) {
    statusText.textContent = 'Creating folder structure...';
    
    try {
    const folderResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/create-folders`, {
    method: 'POST',
    headers: {
    'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
    folder_id: currentFolder,
      folder_paths: Array.from(allPaths)
      })
              });

    if (folderResponse.ok) {
    const folderResult = await folderResponse.json();
    } else {
      console.error('Failed to create folder structure');
      }
    } catch (err) {
      console.error('Error creating folders:', err);
      }
          }

    // Process files in smaller batches with delays
    for (let i = 0; i < totalFiles; i += maxBatchSize) {
            if (isCancelled()) return;

    const batch = filesWithPaths.slice(i, Math.min(i + maxBatchSize, totalFiles));
    
    const formData = new FormData();
    formData.append('folder_id', currentFolder);
    
    batch.forEach((item, index) => {
    formData.append('documents[]', item.file);
    // Extract subfolder path from relativePath (keep the full path)
    const pathParts = item.relativePath.split('/');
    // Remove the filename to get the folder path
    pathParts.pop();
    const subfolderPath = pathParts.join('/');
      formData.append('file_paths[]', subfolderPath);
            });

    try {
    // Add timeout to prevent connection errors
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout
    
    const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/upload-batch`, {
      method: 'POST',
                headers: {
        'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
    },
    body: formData,
    signal: controller.signal
    });
    
    clearTimeout(timeoutId);

    if (response.ok) {
      try {
        const result = await response.json();
        successful += result.success.length;
        errors = errors.concat(result.errors);
      } catch (jsonErr) {
        batch.forEach(item => {
          errors.push({ file: item.file.name, error: 'Server response error: ' + jsonErr.message });
        });
      }
    } else {
      try {
        const errorData = await response.json();
        batch.forEach(item => {
          errors.push({ file: item.file.name, error: errorData.message || 'Upload failed' });
        });
      } catch (jsonErr) {
        // Handle HTML error responses (like 500 errors that return HTML instead of JSON)
        const errorMessage = `Server error (${response.status}): ${response.statusText || 'Unable to process request'}`;
        batch.forEach(item => {
          errors.push({ file: item.file.name, error: errorMessage });
        });
      }
    }
    } catch (err) {
      if (err.name === 'AbortError') {
          batch.forEach(item => {
                  errors.push({ file: item.file.name, error: 'Upload timeout - file too large or connection too slow' });
                });
              } else {
                batch.forEach(item => {
                  errors.push({ file: item.file.name, error: 'Connection error: ' + err.message });
                });
              }
            }

            processed += batch.length;
            
            // Calculate batch size in bytes
            const batchBytes = batch.reduce((sum, item) => sum + (item.file.size || 0), 0);
            totalBytes += batchBytes;
            
            // Update progress bar
            const progress = (processed / totalFiles) * 100;
            progressFill.style.width = progress + '%';
            
            // Update main status
            statusText.textContent = `Processing batch ${Math.ceil(processed / maxBatchSize)} of ${Math.ceil(totalFiles / maxBatchSize)}`;
            
            // Update details with current batch info
            const currentBatchFiles = batch.map(item => item.file.name).slice(0, 3);
            const displayNames = currentBatchFiles.join(', ');
            const moreCount = batch.length > 3 ? ` and ${batch.length - 3} more` : '';
            detailsText.textContent = `${processed}/${totalFiles} files • Current: ${displayNames}${moreCount}`;
            
            // Calculate and display speed
            const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
            if (elapsedTime > 0 && totalBytes > 0) {
              const bytesPerSecond = totalBytes / elapsedTime;
              const mbPerSecond = (bytesPerSecond / (1024 * 1024)).toFixed(1);
              const remaining = totalFiles - processed;
              const estimatedTimeLeft = remaining > 0 ? Math.round((remaining / processed) * elapsedTime) : 0;
              speedText.textContent = `${mbPerSecond} MB/s • ${estimatedTimeLeft}s remaining`;
            }
            
            // Add delay between batches to prevent server overload
            if (i + maxBatchSize < totalFiles) {
              await new Promise(resolve => setTimeout(resolve, 1000)); // 1 second delay
            }
          }

      // Show completion status
      progressFill.style.width = '100%';
      
      const totalFoldersCreated = allPaths.size;
      const elapsedTime = (Date.now() - startTime) / 1000;
      const avgSpeed = totalBytes > 0 ? ((totalBytes / (1024 * 1024)) / elapsedTime).toFixed(1) : 0;
      
      if (errors.length === 0) {
        statusText.textContent = `✅ Upload Complete!`;
        detailsText.textContent = `${successful} files uploaded, ${totalFoldersCreated} folders created`;
        speedText.textContent = `Avg speed: ${avgSpeed} MB/s • Total time: ${Math.round(elapsedTime)}s`;
        showToast(`Successfully uploaded ${successful} files and created ${totalFoldersCreated} folders!`);
      } else {
        statusText.textContent = `⚠️ Upload Complete with Errors`;
        detailsText.textContent = `${successful} files uploaded, ${totalFoldersCreated} folders created, ${errors.length} failed`;
        speedText.textContent = `Avg speed: ${avgSpeed} MB/s • Total time: ${Math.round(elapsedTime)}s`;
        showToast(`Uploaded ${successful} files, created ${totalFoldersCreated} folders, ${errors.length} failed`, 'warning');
        
        // Log errors
        
        // Add errors to upload error tracking for retry functionality
        if (errors.length > 0) {
          const detailedErrors = errors.map(error => {
            // Find the original file object from the batch data
            const originalFile = filesWithPaths.find(item => item.file.name === error.file);
            return {
              file: originalFile ? originalFile.file : { name: error.file },
              fileName: error.file,
              error: error.error,
              timestamp: new Date(),
              folderId: currentFolder
            };
          });
          
          setUploadErrors(prev => [...prev, ...detailedErrors]);
          setShowErrorReport(true);
        }
      }

      // Auto-close modal after 3 seconds
      setTimeout(() => {
        if (modal.parentNode) {
          document.body.removeChild(modal);
        }
      }, 3000);

      // Clear the temporary folder paths
      delete window.currentDropFolderPaths;

      // Refresh folder contents
      await loadFolderContents(currentFolder);

      // Broadcast update
      const channel = new BroadcastChannel('rwcvault_updates');
      channel.postMessage({ type: 'documents_updated' });
      channel.close();

    } catch (err) {
      console.error('Batch upload error:', err);
      statusText.textContent = 'Upload failed with error';
      showToast('Upload failed with error', 'error');
    }
  };

  const processBatchUpload = async (files, progressFill, statusText, detailsText, speedText, modal, isCancelled) => {
    const maxBatchSize = 25; // Optimized batch size for better performance while maintaining stability
    const totalFiles = files.length;
    let processed = 0;
    let errors = [];
    let successful = 0;
    let startTime = Date.now();
    let totalBytes = 0;

    try {
      // Process files in batches
      for (let i = 0; i < totalFiles; i += maxBatchSize) {
        if (isCancelled()) return;

        const batch = files.slice(i, Math.min(i + maxBatchSize, totalFiles));
        
        const formData = new FormData();
        formData.append('folder_id', currentFolder);
        
        const filePaths = [];
        
        batch.forEach((file, index) => {
          formData.append('documents[]', file);
          // Extract relative path from webkitRelativePath
          const relativePath = file.webkitRelativePath || '';
          const pathParts = relativePath.split('/');
          // Remove the filename to get the folder path
          pathParts.pop();
          // Remove the root folder name to get subfolder path
          pathParts.shift();
          const subfolderPath = pathParts.join('/');
          filePaths.push(subfolderPath);
        });
        
        // Add file paths to form data
        filePaths.forEach((path, index) => {
          formData.append('file_paths[]', path);
        });

        try {
          const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/upload-batch`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
            },
            body: formData
          });

          if (response.ok) {
            try {
              const result = await response.json();
              successful += result.success.length;
              errors = errors.concat(result.errors);
            } catch (jsonErr) {
              batch.forEach(file => {
                errors.push({ file: file.name, error: 'Server response error: ' + jsonErr.message });
              });
            }
          } else {
            try {
              const errorData = await response.json();
              batch.forEach(file => {
                errors.push({ file: file.name, error: errorData.message || 'Upload failed' });
              });
            } catch (jsonErr) {
              // Handle HTML error responses (like 500 errors that return HTML instead of JSON)
              const errorMessage = `Server error (${response.status}): ${response.statusText || 'Unable to process request'}`;
              batch.forEach(file => {
                errors.push({ file: file.name, error: errorMessage });
              });
            }
          }
        } catch (err) {
          batch.forEach(file => {
            errors.push({ file: file.name, error: 'Connection error: ' + err.message });
          });
        }

        processed += batch.length;
        
        // Calculate batch size in bytes
        const batchBytes = batch.reduce((sum, file) => sum + (file.size || 0), 0);
        totalBytes += batchBytes;
        
        // Update progress bar
        const progress = (processed / totalFiles) * 100;
        progressFill.style.width = progress + '%';
        
        // Update main status
        statusText.textContent = `Processing batch ${Math.ceil(processed / maxBatchSize)} of ${Math.ceil(totalFiles / maxBatchSize)}`;
        
        // Update details with current batch info
        const currentBatchFiles = batch.map(file => file.name).slice(0, 3);
        const displayNames = currentBatchFiles.join(', ');
        const moreCount = batch.length > 3 ? ` and ${batch.length - 3} more` : '';
        detailsText.textContent = `${processed}/${totalFiles} files • Current: ${displayNames}${moreCount}`;
        
        // Calculate and display speed
        const elapsedTime = (Date.now() - startTime) / 1000; // in seconds
        if (elapsedTime > 0 && totalBytes > 0) {
          const bytesPerSecond = totalBytes / elapsedTime;
          const mbPerSecond = (bytesPerSecond / (1024 * 1024)).toFixed(1);
          const remaining = totalFiles - processed;
          const estimatedTimeLeft = remaining > 0 ? Math.round((remaining / processed) * elapsedTime) : 0;
          speedText.textContent = `${mbPerSecond} MB/s • ${estimatedTimeLeft}s remaining`;
        }
      }

      // Show completion status
      progressFill.style.width = '100%';
      
      const elapsedTime = (Date.now() - startTime) / 1000;
      const avgSpeed = totalBytes > 0 ? ((totalBytes / (1024 * 1024)) / elapsedTime).toFixed(1) : 0;
      
      if (errors.length === 0) {
        statusText.textContent = `✅ Upload Complete!`;
        detailsText.textContent = `${successful} files uploaded successfully`;
        speedText.textContent = `Avg speed: ${avgSpeed} MB/s • Total time: ${Math.round(elapsedTime)}s`;
        showToast(`Successfully uploaded ${successful} files!`);
      } else {
        statusText.textContent = `⚠️ Upload Complete with Errors`;
        detailsText.textContent = `${successful} files uploaded, ${errors.length} failed`;
        speedText.textContent = `Avg speed: ${avgSpeed} MB/s • Total time: ${Math.round(elapsedTime)}s`;
        showToast(`Uploaded ${successful} files, ${errors.length} failed`, 'warning');
        
        // Log errors
        
        // Add errors to upload error tracking for retry functionality
        if (errors.length > 0) {
          const detailedErrors = errors.map(error => {
            // Find the original file object from the batch data
            const originalFile = files.find(f => f.name === error.file);
            return {
              file: originalFile || { name: error.file },
              fileName: error.file,
              error: error.error,
              timestamp: new Date(),
              folderId: currentFolder
            };
          });
          
          setUploadErrors(prev => [...prev, ...detailedErrors]);
          setShowErrorReport(true);
        }
      }

      // Auto-close modal after 3 seconds
      setTimeout(() => {
        if (modal.parentNode) {
          document.body.removeChild(modal);
        }
      }, 3000);

      // Refresh folder contents
      await loadFolderContents(currentFolder);

      // Broadcast update
      const channel = new BroadcastChannel('rwcvault_updates');
      channel.postMessage({ type: 'documents_updated' });
      channel.close();

    } catch (err) {
      console.error('Batch upload error:', err);
      statusText.textContent = 'Upload failed with error';
      showToast('Upload failed with error', 'error');
    }
  };

  const handleRename = (item) => {
    setContextMenu(null);
    setTargetItem(item);
    setShowRenameModal(true);
  };

  const handleMove = (item) => {
    setContextMenu(null);
    setTargetItem(item);
    setShowMoveModal(true);
  };

  const handleMoveMultiple = () => {
    setContextMenu(null);
    // Create a special item that represents multiple selections
    const multiItem = {
      type: 'multiple',
      name: `${selectedItems.length} items`,
      id: 'multiple',
      selectedItems: selectedItems
    };
    setTargetItem(multiItem);
    setShowMoveModal(true);
  };

  const handleEditPermissions = (folder) => {
    setContextMenu(null);
    setTargetItem(folder);
    setShowPermissionsModal(true);
  };

  const handleDownload = async (document) => {
    setContextMenu(null);
    
    // Check if file is large (> 10MB) to show progress
    const fileSize = document.file_size || 0;
    const showProgress = fileSize > 10 * 1024 * 1024; // 10MB threshold
    
    let progressModal = null;
    let progressBar = null;
    let progressText = null;
    let blobUrl = null; // Store blob URL for completion buttons
    
    if (showProgress) {
      // Create progress modal for large files
      progressModal = document.createElement('div');
      progressModal.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: white;
        border-radius: 8px;
        padding: 24px;
        box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        z-index: 3000;
        min-width: 350px;
      `;
      
      const title = document.createElement('h3');
      title.textContent = 'Downloading File';
      title.style.marginBottom = '16px';
      progressModal.appendChild(title);
      
      const fileName = document.createElement('div');
      fileName.textContent = document.original_name || document.name;
      fileName.style.cssText = 'font-size: 14px; color: #666; margin-bottom: 12px; word-break: break-word;';
      progressModal.appendChild(fileName);
      
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = `
        width: 100%;
        height: 20px;
        background: #f0f0f0;
        border-radius: 10px;
        overflow: hidden;
        margin-bottom: 12px;
      `;
      
      progressBar = document.createElement('div');
      progressBar.style.cssText = `
        height: 100%;
        background: #4f46e5;
        width: 0%;
        transition: width 0.3s ease;
      `;
      progressContainer.appendChild(progressBar);
      progressModal.appendChild(progressContainer);
      
      progressText = document.createElement('div');
      progressText.style.cssText = 'font-size: 14px; color: #666; text-align: center;';
      progressText.textContent = 'Starting download...';
      progressModal.appendChild(progressText);
      
      document.body.appendChild(progressModal);
    }
    
    try {
      // Use XMLHttpRequest for progress tracking on large files
      if (showProgress) {
        const xhr = new XMLHttpRequest();
        
        await new Promise((resolve, reject) => {
          xhr.open('POST', `${window.rwcvaultData.apiUrl}/documents/${document.id}/download`, true);
          xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('rwcvault_token')}`);
          xhr.setRequestHeader('X-Download-Action', 'download');
          xhr.responseType = 'blob';
          
          xhr.onprogress = (event) => {
            if (event.lengthComputable && progressBar && progressText) {
              const percentComplete = (event.loaded / event.total) * 100;
              progressBar.style.width = percentComplete + '%';
              
              const mbLoaded = (event.loaded / (1024 * 1024)).toFixed(1);
              const mbTotal = (event.total / (1024 * 1024)).toFixed(1);
              progressText.textContent = `${mbLoaded} MB / ${mbTotal} MB (${Math.round(percentComplete)}%)`;
            }
          };
          
          xhr.onload = () => {
            if (xhr.status === 200) {
              const blob = xhr.response;
              const url = window.URL.createObjectURL(blob);
              
              // Create temporary link and trigger download
              const link = document.createElement('a');
              link.href = url;
              link.download = document.original_name || document.name;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Clean up
              setTimeout(() => window.URL.revokeObjectURL(url), 100);
              
              if (progressModal) {
                progressText.textContent = 'Download complete!';
                setTimeout(() => {
                  if (progressModal.parentNode) {
                    document.body.removeChild(progressModal);
                  }
                }, 1000);
              }
              
              resolve();
            } else {
              reject(new Error('Download failed'));
            }
          };
          
          xhr.onerror = () => reject(new Error('Network error'));
          xhr.send();
        });
      } else {
        // For smaller files, use the simpler fetch approach
        const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${document.id}/download`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
            'X-Download-Action': 'download'
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = url;
          link.download = document.original_name || document.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } else {
          showToast('Failed to download document', 'error');
        }
      }
    } catch (err) {
      console.error('Download error:', err);
      showToast('Error downloading document', 'error');
      
      if (progressModal && progressModal.parentNode) {
        document.body.removeChild(progressModal);
      }
      
      // Clean up blob URL if it exists
      if (blobUrl) {
        window.URL.revokeObjectURL(blobUrl);
      }
    }
  };

  const handleDownloadZip = async (folder) => {
    setContextMenu(null);
    // TODO: Implement ZIP download
    showToast('ZIP download coming soon!', 'info');
  };

  const handleDelete = async (item) => {
    setContextMenu(null);
    
    if (item.type === 'folder') {
      // First, try to delete without recursive flag to check if folder has contents
      try {
        const endpoint = `/admin/folders/${item.id}`;
        const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
          }
        });

        if (response.ok) {
          showToast('Folder deleted successfully');
          loadFolderContents(currentFolder);
        } else if (response.status === 400) {
          const data = await response.json();
          
          // Check if error is due to folder having contents
          if (data.code === 'folder_not_empty') {
            // Extract counts from error message or data
            let document_count = 0;
            let subfolder_count = 0;
            
            // Debug log to see what we're getting
            
            // Try multiple parsing strategies
            if (data.data) {
              document_count = data.data.document_count || 0;
              subfolder_count = data.data.subfolder_count || 0;
            } 
            
            // If still zero, try parsing from message
            if (document_count === 0 && subfolder_count === 0 && data.message) {
              // Try pattern: "Folder contains X document(s) and Y subfolder(s)"
              const pattern1 = data.message.match(/(\d+)\s+document\(s\)\s+and\s+(\d+)\s+subfolder\(s\)/);
              if (pattern1) {
                document_count = parseInt(pattern1[1], 10);
                subfolder_count = parseInt(pattern1[2], 10);
              } else {
                // Try pattern: "This folder contains X documents and Y subfolders"
                const pattern2 = data.message.match(/(\d+)\s+documents?\s+and\s+(\d+)\s+subfolders?/);
                if (pattern2) {
                  document_count = parseInt(pattern2[1], 10);
                  subfolder_count = parseInt(pattern2[2], 10);
                } else {
                  // Try to find any numbers in the message
                  const numbers = data.message.match(/\d+/g);
                  if (numbers && numbers.length >= 2) {
                    document_count = parseInt(numbers[0], 10);
                    subfolder_count = parseInt(numbers[1], 10);
                  }
                }
              }
            }
            
            // Ensure we have valid numbers
            document_count = isNaN(document_count) ? 0 : document_count;
            subfolder_count = isNaN(subfolder_count) ? 0 : subfolder_count;
            
            // Show confirmation dialog for recursive deletion
            const message = `This folder contains:\n` +
              `• ${document_count} document(s)\n` +
              `• ${subfolder_count} subfolder(s)\n\n` +
              `Are you sure you want to delete this folder and ALL its contents? This action cannot be undone.`;
            
            if (window.confirm(message)) {
              // Try again with recursive flag
              const recursiveResponse = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}?recursive=true`, {
                method: 'DELETE',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
                }
              });

              if (recursiveResponse.ok) {
                showToast('Folder and all contents deleted successfully');
                loadFolderContents(currentFolder);
              } else {
                const recursiveData = await recursiveResponse.json();
                showToast(recursiveData.message || 'Failed to delete folder', 'error');
              }
            }
          } else {
            showToast(data.message || 'Failed to delete folder', 'error');
          }
        } else {
          const data = await response.json();
          showToast(data.message || 'Failed to delete', 'error');
        }
      } catch (err) {
        showToast('Error deleting folder', 'error');
      }
    } else {
      // Document deletion (unchanged)
      if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;
      
      try {
        const endpoint = `/admin/documents/${item.id}`;
        const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
          }
        });

        if (response.ok) {
          showToast('Document deleted successfully');
          loadFolderContents(currentFolder);
        } else {
          const data = await response.json();
          showToast(data.message || 'Failed to delete', 'error');
        }
      } catch (err) {
        showToast('Error deleting item', 'error');
      }
    }
  };

  const handleDeleteMultiple = async () => {
    setContextMenu(null);
    if (!window.confirm(`Are you sure you want to delete ${selectedItems.length} items?`)) return;

    const errors = [];
    const successes = [];

    for (const key of selectedItems) {
      const [type, id] = key.split('-');
      try {
        const endpoint = type === 'folder'
          ? `/admin/folders/${id}`
          : `/admin/documents/${id}`;

        const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
          }
        });

        if (response.ok) {
          successes.push(key);
        } else {
          errors.push(key);
        }
      } catch (err) {
        errors.push(key);
      }
    }

    if (successes.length > 0) {
      showToast(`Deleted ${successes.length} items successfully`);
    }
    if (errors.length > 0) {
      showToast(`Failed to delete ${errors.length} items`, 'error');
    }

    setSelectedItems([]);
    loadFolderContents(currentFolder);
  };

  const handleDownloadMultiple = async () => {
    setContextMenu(null);
    
    for (const key of selectedItems) {
      const [type, id] = key.split('-');
      if (type === 'document') {
        try {
          const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${id}/download`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
              'X-Download-Action': 'download'
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            // Find document name
            const doc = documents.find(d => d.id === parseInt(id));
            const filename = doc ? doc.original_name : `document_${id}.pdf`;
            
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
          }
        } catch (err) {
          console.error(`Error downloading document ${id}:`, err);
        }
      }
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortedItems = () => {
    const items = [
      ...folders.map(f => ({ ...f, type: 'folder', name: f.name })),
      ...documents.map(d => ({ ...d, type: 'document', name: d.original_name }))
    ];

    return items.sort((a, b) => {
      let compareValue = 0;

      switch (sortBy) {
        case 'name':
          compareValue = getMonthSortValue(a.name).localeCompare(getMonthSortValue(b.name));
          break;
        case 'date':
          const dateA = new Date(a.created_at || a.uploaded_at);
          const dateB = new Date(b.created_at || b.uploaded_at);
          compareValue = dateA - dateB;
          break;
        case 'size':
          const sizeA = a.type === 'document' ? a.file_size : 0;
          const sizeB = b.type === 'document' ? b.file_size : 0;
          compareValue = sizeA - sizeB;
          break;
        default:
          compareValue = 0;
      }

      return sortOrder === 'asc' ? compareValue : -compareValue;
    });
  };

  const handleItemClick = (item, event) => {
    if (event.ctrlKey || event.metaKey) {
      // Multi-select
      const itemKey = `${item.type}-${item.id}`;
      setSelectedItems(prev =>
        prev.includes(itemKey)
          ? prev.filter(i => i !== itemKey)
          : [...prev, itemKey]
      );
    } else if (item.type === 'folder') {
      // Navigate to folder only if not multi-selecting
      if (selectedItems.length === 0) {
        navigateToFolder(item.id);
      } else {
        // Clear selection and navigate
        setSelectedItems([]);
        navigateToFolder(item.id);
      }
    } else {
      // For documents, just clear selection if clicking without Ctrl
      setSelectedItems([]);
    }
  };

  const handleItemDoubleClick = async (item) => {
    if (item.type === 'document') {
      // View document in new tab
      const fileSize = item.file_size || 0;
      const showProgress = fileSize > 10 * 1024 * 1024; // 10MB threshold
      
      let progressModal = null;
      let progressBar = null;
      let progressText = null;
      
      let blobUrl = null; // Store blob URL for completion buttons
      
      if (showProgress) {
        // Create progress modal for large files
        progressModal = document.createElement('div');
        progressModal.style.cssText = `
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: white;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          z-index: 3000;
          min-width: 350px;
        `;
        
        const title = document.createElement('h3');
        title.textContent = 'Loading File for Viewing';
        title.style.marginBottom = '16px';
        progressModal.appendChild(title);
        
        const fileName = document.createElement('div');
        fileName.textContent = item.original_name || item.name;
        fileName.style.cssText = 'font-size: 14px; color: #666; margin-bottom: 12px; word-break: break-word;';
        progressModal.appendChild(fileName);
        
        const progressContainer = document.createElement('div');
        progressContainer.style.cssText = `
          width: 100%;
          height: 20px;
          background: #f0f0f0;
          border-radius: 10px;
          overflow: hidden;
          margin-bottom: 12px;
        `;
        
        progressBar = document.createElement('div');
        progressBar.style.cssText = `
          height: 100%;
          background: #4f46e5;
          width: 0%;
          transition: width 0.3s ease;
        `;
        progressContainer.appendChild(progressBar);
        progressModal.appendChild(progressContainer);
        
        progressText = document.createElement('div');
        progressText.style.cssText = 'font-size: 14px; color: #666; text-align: center;';
        progressText.textContent = 'Loading file...';
        progressModal.appendChild(progressText);
        
        document.body.appendChild(progressModal);
      }
      
      try {
        if (showProgress) {
          // Use XMLHttpRequest for progress tracking
          const xhr = new XMLHttpRequest();
          
          await new Promise((resolve, reject) => {
            xhr.open('POST', `${window.rwcvaultData.apiUrl}/documents/${item.id}/download`, true);
            xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('rwcvault_token')}`);
            xhr.setRequestHeader('X-Download-Action', 'view');
            xhr.responseType = 'blob';
            
            xhr.onprogress = (event) => {
              if (event.lengthComputable && progressBar && progressText) {
                const percentComplete = (event.loaded / event.total) * 100;
                progressBar.style.width = percentComplete + '%';
                
                const mbLoaded = (event.loaded / (1024 * 1024)).toFixed(1);
                const mbTotal = (event.total / (1024 * 1024)).toFixed(1);
                progressText.textContent = `${mbLoaded} MB / ${mbTotal} MB (${Math.round(percentComplete)}%)`;
              }
            };
            
            xhr.onload = () => {
              if (xhr.status === 200) {
                const blob = xhr.response;
                blobUrl = window.URL.createObjectURL(blob);
                
                // Show completion buttons
                if (progressModal && progressText) {
                  progressText.textContent = 'Download complete!';
                  
                  // Hide progress bar
                  if (progressBar) {
                    progressBar.parentNode.style.display = 'none';
                  }
                  
                  // Create buttons container
                  const buttonsContainer = document.createElement('div');
                  buttonsContainer.style.cssText = 'display: flex; gap: 12px; margin-top: 16px;';
                  
                  // Create "Open Document" button
                  const openButton = document.createElement('button');
                  const isPDF = blob.type === 'application/pdf';
                  openButton.textContent = isPDF ? 'View Document' : 'Download File';
                  openButton.style.cssText = `
                    flex: 1;
                    background: #4f46e5;
                    color: white;
                    border: none;
                    padding: 12px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                  `;
                  
                  openButton.onclick = () => {
                    // This is a direct user action - no popup blocker!
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.target = '_blank';
                    
                    if (!isPDF) {
                      link.download = item.original_name || item.name;
                    }
                    
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    
                    // Close modal and cleanup
                    if (progressModal.parentNode) {
                      document.body.removeChild(progressModal);
                    }
                    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
                  };
                  
                  // Create "Cancel" button
                  const cancelButton = document.createElement('button');
                  cancelButton.textContent = 'Cancel';
                  cancelButton.style.cssText = `
                    flex: 1;
                    background: #6b7280;
                    color: white;
                    border: none;
                    padding: 12px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                  `;
                  
                  cancelButton.onclick = () => {
                    // Cleanup and close
                    if (progressModal.parentNode) {
                      document.body.removeChild(progressModal);
                    }
                    if (blobUrl) {
                      window.URL.revokeObjectURL(blobUrl);
                    }
                  };
                  
                  // Add buttons to container
                  buttonsContainer.appendChild(openButton);
                  buttonsContainer.appendChild(cancelButton);
                  
                  // Add container to modal
                  progressModal.appendChild(buttonsContainer);
                }
                
                resolve();
              } else {
                reject(new Error('Failed to load document'));
              }
            };
            
            xhr.onerror = () => reject(new Error('Network error'));
            xhr.send();
          });
        } else {
          // For smaller files, use the simpler approach
          const response = await fetch(`${window.rwcvaultData.apiUrl}/documents/${item.id}/download`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
              'X-Download-Action': 'view'
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary link and trigger click to avoid popup blockers
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            
            // For PDFs, try to open in browser; for other files, download
            const isPDF = blob.type === 'application/pdf';
            if (!isPDF) {
              link.download = item.original_name || item.name;
            }
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up blob URL after a delay
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
          } else {
            showToast('Failed to open document', 'error');
          }
        }
      } catch (err) {
        console.error('Error opening document:', err);
        showToast('Error opening document', 'error');
        
        if (progressModal && progressModal.parentNode) {
          document.body.removeChild(progressModal);
        }
        
        // Clean up blob URL if it exists
        if (blobUrl) {
          window.URL.revokeObjectURL(blobUrl);
        }
      }
    } else if (item.type === 'folder') {
      // Navigate to folder
      navigateToFolder(item.id);
    }
  };

  const isItemSelected = (item) => {
    return selectedItems.includes(`${item.type}-${item.id}`);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, item) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItem(item);

    // If the dragged item is not selected, clear selection and select only this item
    const itemKey = `${item.type}-${item.id}`;
    let currentSelection = selectedItems;
    
    if (!selectedItems.includes(itemKey)) {
      // Item not in selection, so make it the only selected item
      currentSelection = [itemKey];
      setSelectedItems([itemKey]);
    }
    
    // Store the current selection in the drag data with a unique identifier
    const dragData = {
      type: 'internal-move',
      selection: currentSelection,
      draggedItem: {
        ...item,
        name: item.name || item.original_name // Ensure we have the name
      }
    };
    
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.setData('text/plain', `Moving ${currentSelection.length > 1 ? currentSelection.length + ' items' : (item.name || item.original_name)}`);

    // Add a slight delay to allow the drag image to be created
    setTimeout(() => {
      e.target.style.opacity = '0.5';
      // Also fade all selected items
      if (currentSelection.length > 1) {
        currentSelection.forEach(key => {
          const el = document.querySelector(`[data-item-key="${key}"]`);
          if (el) el.style.opacity = '0.5';
        });
      }
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    
    // Restore opacity for all selected items
    if (selectedItems.length > 1) {
      selectedItems.forEach(key => {
        const el = document.querySelector(`[data-item-key="${key}"]`);
        if (el) el.style.opacity = '1';
      });
    }
    
    setIsDragging(false);
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, folder) => {
    e.preventDefault();
    if (draggedItem && folder && folder.id !== draggedItem.id) {
      setDropTarget(folder.id);
    }
  };

  const handleDragLeave = (e, folder) => {
    // Only clear if we're leaving the folder entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dropTarget === folder.id) {
        setDropTarget(null);
      }
    }
  };

  const handleDrop = async (e, targetFolder) => {
    // First check if this is an external file/folder drop
    const hasFiles = e.dataTransfer.types.includes('Files');
    const hasItems = e.dataTransfer.items && e.dataTransfer.items.length > 0;
    
    // If this is an external drop, don't handle it here - let it bubble up to the main drop handler
    if (hasFiles || hasItems) {
      const jsonData = e.dataTransfer.getData('application/json');
      const isInternalDrag = jsonData || draggedItem;
      
      if (!isInternalDrag) {
        // This is an external file drop, don't prevent default - let main handler deal with it
        return;
      }
    }

    e.preventDefault();
    e.stopPropagation();

    if (!targetFolder) {
      return;
    }

    // Try to get the drag data
    let dragData = null;
    let draggedSelection = [];
    let draggedItemFromData = null;
    
    try {
      const jsonData = e.dataTransfer.getData('application/json');
      
      if (jsonData) {
        dragData = JSON.parse(jsonData);
        
        // Check if this is our internal move operation
        if (dragData.type === 'internal-move') {
          draggedSelection = dragData.selection || [];
          draggedItemFromData = dragData.draggedItem;
        } else {
          // Legacy format - assume it's just the selection array
          draggedSelection = Array.isArray(dragData) ? dragData : [];
        }
      }
    } catch (err) {
      console.error('Failed to parse drag data:', err);
    }

    // Use draggedItem from drag data if available, otherwise fall back to state
    const effectiveDraggedItem = draggedItemFromData || draggedItem;

    // If no drag data, this might be an external file drop - ignore it here
    if (!dragData && !effectiveDraggedItem) {
      return;
    }

    // Determine what to move
    let itemsToMove = [];
    
    // Use the drag data selection if available, otherwise fall back to current selection
    const effectiveSelection = draggedSelection.length > 0 ? draggedSelection : selectedItems;
    
    if (effectiveSelection.length > 0) {
      // Map selection keys to actual items
      itemsToMove = effectiveSelection.map(key => {
        const [type, id] = key.split('-');
        const numericId = parseInt(id);
        
        let item = null;
        
        if (type === 'folder') {
          item = folders.find(f => f.id === numericId);
          if (item) {
            return { ...item, type: 'folder' };
          }
        } else if (type === 'document') {
          item = documents.find(d => d.id === numericId);
          if (item) {
            return { ...item, type: 'document', name: item.original_name };
          }
        }
        
        // If item not found in current arrays, check if it's the dragged item
        if (!item && effectiveDraggedItem && effectiveDraggedItem.id === numericId && effectiveDraggedItem.type === type) {
          return { ...effectiveDraggedItem, name: effectiveDraggedItem.name || effectiveDraggedItem.original_name };
        }
        
        // If still not found, create minimal item object for cross-folder drags
        if (!item && dragData && dragData.type === 'internal-move') {
          // Try to get the name from the original dragData if available
          const originalName = dragData.draggedItem && dragData.draggedItem.id === numericId ? 
            dragData.draggedItem.name : `${type}_${numericId}`;
          
          return {
            id: numericId,
            type: type,
            name: originalName // Use original name or fallback
          };
        }
        
        return null;
      }).filter(item => item && item.id !== targetFolder.id);
    } else if (effectiveDraggedItem) {
      // Only one item being dragged
      itemsToMove = [effectiveDraggedItem];
    }

    if (itemsToMove.length === 0) {
      return;
    }

    // Show confirmation dialog before moving
    const itemNames = await Promise.all(itemsToMove.map(async (item) => {
      // Debug logging to see what names we're getting
      
      // Try multiple ways to get the name
      let itemName = item.name || item.original_name;
      
      // If we still don't have a good name, try to find it from the current folder/document lists
      if (!itemName || itemName.startsWith('folder_') || itemName.startsWith('document_')) {
        if (item.type === 'folder') {
          const folderFromList = folders.find(f => f.id === item.id);
          if (folderFromList) {
            itemName = folderFromList.name;
          } else {
            // Folder not in current view, fetch from API
            try {
              const folderResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
                }
              });
              
              if (folderResponse.ok) {
                const allFolders = await folderResponse.json();
                const foundFolder = allFolders.find(f => f.id === item.id);
                if (foundFolder) {
                  itemName = foundFolder.name;
                }
              }
            } catch (err) {
              console.error('Error fetching folder name for confirmation:', err);
            }
          }
        } else if (item.type === 'document') {
          const docFromList = documents.find(d => d.id === item.id);
          if (docFromList) {
            itemName = docFromList.original_name;
          }
        }
      }
      
      return itemName || `Unknown ${item.type} (ID: ${item.id})`;
    }));

    // Filter out items that are already in the target folder (prevents no-op moves)
    itemsToMove = itemsToMove.filter(item => {
      if (item.type === 'folder') {
        // Check if the folder's parent is already the target
        const folderFromList = folders.find(f => f.id === item.id);
        if (folderFromList && String(folderFromList.parent_id) === String(targetFolder.id)) {
          return false; // Already in this folder, skip
        }
      } else if (item.type === 'document') {
        // Check if the document is already in the target folder
        const docFromList = documents.find(d => d.id === item.id);
        if (docFromList && String(docFromList.folder_id) === String(targetFolder.id)) {
          return false; // Already in this folder, skip
        }
      }
      return true;
    });

    if (itemsToMove.length === 0) {
      return; // Nothing to move (all items already in target)
    }

    const confirmMessage = itemsToMove.length === 1
      ? `Are you sure you want to move "${itemNames[0]}" into "${targetFolder.name}"?`
      : `Are you sure you want to move ${itemsToMove.length} items into "${targetFolder.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return; // User cancelled the move
    }

    // Move all items
    let movedCount = 0;
    let errorCount = 0;
    const movePromises = [];

    // Create all move operations
    for (const item of itemsToMove) {
      if (item.id === targetFolder.id) {
        continue; // Skip if trying to move folder into itself
      }

      const moveOperation = async () => {
        try {
          let endpoint, body, method;
          
          if (item.type === 'folder') {
            // Get the correct folder name, preferring the current folders list
            let folderName = item.name;
            const folderFromList = folders.find(f => f.id === item.id);
            if (folderFromList) {
              folderName = folderFromList.name; // Use the name from the current folder list
            } else {
              
              // Fetch folder details from API since it's not in current view
              try {
                const folderResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
                  }
                });
                
                if (folderResponse.ok) {
                  const allFolders = await folderResponse.json();
                  const foundFolder = allFolders.find(f => f.id === item.id);
                  if (foundFolder) {
                    folderName = foundFolder.name;
                    console.log(`Found folder via API: ID ${item.id} -> Name "${folderName}"`);
                  } else {
                    console.error(`Folder ID ${item.id} not found even in complete folder list`);
                  }
                } else {
                  console.error('Failed to fetch complete folder list');
                }
              } catch (err) {
                console.error('Error fetching folder details:', err);
              }
            }
            
            endpoint = `/admin/folders/${item.id}`;
            body = { 
              name: folderName, // Use the correct folder name
              parent_id: targetFolder.id 
            };
            method = 'PUT';
            
            console.log(`Moving folder ${item.id} with name "${folderName}" to folder ${targetFolder.id}`);
            console.log('Request body:', body);
          } else {
            endpoint = `/admin/documents/${item.id}/move`;
            body = { folder_id: targetFolder.id };
            method = 'PUT';
            
            console.log(`Moving document ${item.id} to folder ${targetFolder.id}`);
          }

          const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
            method: method,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });

          if (response.ok) {
            return { success: true, item };
          } else {
            const errorData = await response.json();
            console.error(`Failed to move ${item.type} ${item.id}:`, errorData);
            return { success: false, item, error: errorData.message };
          }
        } catch (err) {
          console.error(`Error moving ${item.type} ${item.id}:`, err);
          return { success: false, item, error: err.message };
        }
      };

      movePromises.push(moveOperation());
    }

    // Wait for all moves to complete
    const results = await Promise.all(movePromises);
    
    // Count successes and failures
    results.forEach(result => {
      if (result.success) {
        movedCount++;
      } else {
        errorCount++;
      }
    });

    // Clear selection after move
    setSelectedItems([]);

    // Show result message
    if (movedCount > 0 && errorCount === 0) {
      showToast(`Moved ${movedCount} item(s) to "${targetFolder.name}"`);
    } else if (movedCount > 0 && errorCount > 0) {
      showToast(`Moved ${movedCount} item(s), ${errorCount} failed`, 'warning');
    } else if (errorCount > 0) {
      showToast(`Failed to move ${errorCount} item(s)`, 'error');
    }

    // Add a small delay before refreshing to ensure backend has processed all changes
    if (movedCount > 0) {
      setTimeout(() => {
        loadFolderContents(currentFolder);
      }, 200);
    }
  };

  // File drop handlers for upload
  const handleFileDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if this is an internal drag operation
    const isInternalDrag = e.dataTransfer.types.includes('application/json') || draggedItem;
    
    if (isInternalDrag) {
      // This is an internal move operation
      e.dataTransfer.dropEffect = 'move';
      return;
    }

    // Only show upload drop effect if we're in a folder and dragging external files
    if (currentFolder && e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragging(true);
    }
  };

  const handleFileDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if this is an internal drag operation
    const isInternalDrag = e.dataTransfer.types.includes('application/json') || draggedItem;
    
    if (isInternalDrag) {
      // Don't show upload overlay for internal drags
      return;
    }

    if (currentFolder && e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleFileDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if this is an internal drag operation
    const isInternalDrag = e.dataTransfer.types.includes('application/json') || draggedItem;
    
    if (isInternalDrag) {
      return;
    }

    // Only set dragging to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleFileDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    // Check if this is an external file/folder drop
    const hasFiles = e.dataTransfer.types.includes('Files');
    const hasItems = e.dataTransfer.items && e.dataTransfer.items.length > 0;
    
    // If we have files or items, this is an external drop - handle it here
    if (hasFiles || hasItems) {
      if (!currentFolder) {
        showToast('Please navigate to a folder before uploading files', 'error');
        return;
      }

      // Get both items and files to handle multiple folder drops properly
      const items = Array.from(e.dataTransfer.items);
      const files = Array.from(e.dataTransfer.files);

      // Force handling here regardless of what was clicked
      await handleExternalFileDrop(items, files);
      return;
    }

    // Check if this is an internal drag operation
    const isInternalDrag = e.dataTransfer.types.includes('application/json') || draggedItem;
    
    if (isInternalDrag) {
      // This should be handled by individual folder drop handlers
      return;
    }
  };

  // Separate function to handle external file drops
  const handleExternalFileDrop = async (items, files) => {
    console.log('Drop detected:', items.length, 'items,', files.length, 'files');
    
    // Check if any items are directories (but this may fail for multiple folders)
    const hasDirectories = items.some(item => {
      const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
      return entry && entry.isDirectory;
    });
    
    // Also check if files have webkitRelativePath (indicates folder upload)
    const hasRelativePaths = files.some(file => file.webkitRelativePath && file.webkitRelativePath.length > 0);
    
    console.log('Has directories from items:', hasDirectories);
    console.log('Has relative paths from files:', hasRelativePaths);
    
    if (hasDirectories || hasRelativePaths) {
      // Handle folder drop - try items array first for better empty folder support
      if (hasDirectories) {
        console.log('Using items array for folder detection');
        await handleFolderDrop(items);
      } else {
        console.log('Using files array for folder detection');
        await handleFolderDropFromFiles(files);
      }
    } else {
      // Handle individual file drop
      const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
      const validFiles = files.filter(file => {
        const extension = file.name.split('.').pop().toLowerCase();
        return allowedTypes.includes(extension);
      });

      if (validFiles.length === 0) {
        showToast('Please drop valid files. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV', 'error');
        return;
      }

      if (validFiles.length < files.length) {
        showToast(`Only ${validFiles.length} of ${files.length} files are valid and will be uploaded`, 'info');
      }

      await uploadFiles(validFiles);
    }
  };

  const handleFolderDrop = async (items) => {
    const filesWithPaths = [];
    const allFolderPaths = new Set(); // Track all folder paths, including empty ones
    
    console.log('Processing', items.length, 'dropped items');
    
    const processEntry = async (entry, path = '') => {
      console.log(`  processEntry called for: ${entry.name} (${entry.isDirectory ? 'folder' : 'file'}) at path: '${path}'`);
      
      if (entry.isFile) {
        try {
          const file = await new Promise((resolve, reject) => {
            entry.file(resolve, reject);
          });
          // Store the relative path separately instead of modifying the file object
          const fullPath = path + file.name;
          filesWithPaths.push({
            file: file,
            relativePath: fullPath
          });
          
          console.log(`    Added file: ${fullPath}`);
          
          // Also track the folder path
          const folderPath = path.replace(/\/$/, ''); // Remove trailing slash
          if (folderPath) {
            allFolderPaths.add(folderPath);
            console.log(`    Added folder path from file: ${folderPath}`);
          }
        } catch (fileError) {
          console.error(`    Error reading file ${entry.name}:`, fileError);
        }
      } else if (entry.isDirectory) {
        const currentFolderPath = path + entry.name;
        allFolderPaths.add(currentFolderPath); // Track this folder even if empty
        console.log(`    Added folder path: ${currentFolderPath}`);
        
        try {
          const reader = entry.createReader();
          console.log(`    Reading directory entries for: ${currentFolderPath}`);
          
          const entries = await new Promise((resolve, reject) => {
            const allEntries = [];
            const readEntries = () => {
              reader.readEntries(entries => {
                if (entries.length === 0) {
                  console.log(`    Finished reading directory: ${currentFolderPath}, total entries: ${allEntries.length}`);
                  resolve(allEntries);
                } else {
                  console.log(`    Found ${entries.length} entries in ${currentFolderPath}`);
                  allEntries.push(...entries);
                  readEntries();
                }
              }, reject);
            };
            readEntries();
          });
          
          // Process all child entries
          for (let j = 0; j < entries.length; j++) {
            const childEntry = entries[j];
            console.log(`    Processing child ${j + 1}/${entries.length} of ${currentFolderPath}: ${childEntry.name}`);
            await processEntry(childEntry, currentFolderPath + '/');
          }
          
          console.log(`    Completed processing directory: ${currentFolderPath}`);
        } catch (dirError) {
          console.error(`    Error reading directory ${currentFolderPath}:`, dirError);
        }
      }
    };

    // Process all dropped items with better error handling
    const processedItems = [];
    const failedItems = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      console.log(`\n=== Examining item ${i + 1}/${items.length} ===`);
      console.log('Item details:', {
        kind: item.kind,
        type: item.type,
        toString: item.toString()
      });
      
      try {
        const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
        console.log(`Item ${i + 1} entry:`, entry);
        
        if (entry) {
          console.log(`Processing item ${i + 1}/${items.length}:`, entry.name, entry.isDirectory ? '(folder)' : '(file)');
          
          await processEntry(entry);
          processedItems.push(entry.name);
          console.log(`✓ Completed processing item ${i + 1}:`, entry.name);
        } else {
          console.warn(`⚠ Item ${i + 1} has no entry - might be an empty folder or unsupported item:`, item);
          failedItems.push(`Item ${i + 1} (no entry)`);
          
          // Try fallback for items without entries
          if (item.kind === 'file' && item.type === '') {
            console.log('  Attempting fallback for potential empty folder...');
            // This might be an empty folder, but we can't reliably detect the name
            // The browser doesn't provide enough info for empty folders without entries
          }
        }
      } catch (error) {
        console.error(`✗ Error processing item ${i + 1}:`, error);
        failedItems.push(`Item ${i + 1} (${error.message})`);
        // Continue processing other items even if one fails
      }
    }

    // Log final processing results
    console.log('\n=== Processing Summary ===');
    console.log('Successfully processed items:', processedItems);
    console.log('Failed items:', failedItems);
    console.log('Total folder paths detected:', Array.from(allFolderPaths));
    console.log('Total files found:', filesWithPaths.length);

    console.log('Final results:');
    console.log('All folder paths detected:', Array.from(allFolderPaths));
    console.log('Files with paths:', filesWithPaths.map(f => f.relativePath));
    console.log('Total folders:', allFolderPaths.size);
    console.log('Total files:', filesWithPaths.length);

    // Check if we have any folders or files to process
    if (allFolderPaths.size > 0 || filesWithPaths.length > 0) {
      
      // If we only have empty folders, create them directly
      if (filesWithPaths.length === 0 && allFolderPaths.size > 0) {
        try {
          showToast(`Creating ${allFolderPaths.size} empty folders...`, 'info');
          
          const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/create-folders`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              folder_id: currentFolder,
              folder_paths: Array.from(allFolderPaths)
            })
          });

          if (response.ok) {
            const result = await response.json();
            showToast(`Created ${result.created_folders.length} empty folders`);
            await loadFolderContents(currentFolder);
          } else {
            const errorData = await response.json();
            console.error('Folder creation failed:', errorData);
            showToast('Failed to create folders: ' + (errorData.message || 'Unknown error'), 'error');
          }
        } catch (err) {
          console.error('Error creating empty folders:', err);
          showToast('Error creating folders', 'error');
        }
        return;
      }
      
      // Filter for allowed file types
      const allowedTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'];
      const validFilesWithPaths = filesWithPaths.filter(item => {
        const extension = item.file.name.split('.').pop().toLowerCase();
        return allowedTypes.includes(extension);
      });

      if (validFilesWithPaths.length === 0) {
        showToast('No valid files found in dropped folders. Allowed types: PDF, DOC, DOCX, XLS, XLSX, CSV', 'error');
        return;
      }

      if (validFilesWithPaths.length < filesWithPaths.length) {
        showToast(`Only ${validFilesWithPaths.length} of ${filesWithPaths.length} files are valid and will be uploaded`, 'info');
      }

      // Show summary of what will be processed
      if (processedItems.length > 0) {
        console.log(`Will process ${processedItems.length} folders/files:`, processedItems);
      }
      if (failedItems.length > 0) {
        console.log(`Failed to process ${failedItems.length} items:`, failedItems);
        showToast(`${failedItems.length} items could not be processed (see console for details)`, 'warning');
      }

      // Show progress modal for folder upload with all detected folder paths
      showProgressModalWithPathsAndFolders(validFilesWithPaths, allFolderPaths);
    } else {
      if (failedItems.length > 0) {
        showToast(`Could not process any of the ${items.length} dropped items. Check console for details.`, 'error');
      } else {
        showToast('No files or folders found in dropped items', 'error');
      }
    }
  };

  // Helper function to pass folder paths to upload process
  const showProgressModalWithPathsAndFolders = (validFilesWithPaths, allFolderPaths) => {
    // Store folder paths globally so the upload process can access them
    window.currentDropFolderPaths = allFolderPaths;
    showProgressModalWithPaths(validFilesWithPaths);
  };

  const showToast = (message, type = 'success') => {
    // Create toast container if it doesn't exist
    let toastContainer = document.getElementById('toast-container');
    if (!toastContainer) {
      toastContainer = document.createElement('div');
      toastContainer.id = 'toast-container';
      toastContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        z-index: 3000;
        display: flex;
        flex-direction: column;
        gap: 10px;
      `;
      document.body.appendChild(toastContainer);
    }

    // Create toast element
    const toast = document.createElement('div');
    toast.style.cssText = `
      background: ${type === 'error' ? '#dc2626' : type === 'info' ? '#3b82f6' : '#10b981'};
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      font-size: 14px;
      max-width: 300px;
      animation: slideIn 0.3s ease-out;
    `;
    toast.textContent = message;

    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `;
    if (!document.head.querySelector('style[data-toast]')) {
      style.setAttribute('data-toast', 'true');
      document.head.appendChild(style);
    }

    toastContainer.appendChild(toast);

    // Remove toast after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        toast.remove();
        // Remove container if empty
        if (toastContainer.children.length === 0) {
          toastContainer.remove();
        }
      }, 300);
    }, 3000);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px'
    },
    header: {
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      padding: '16px',
      marginBottom: '20px',
      borderRadius: '8px'
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    title: {
      fontSize: '24px',
      fontWeight: '500',
      color: '#333',
      margin: 0
    },
    backButton: {
      background: '#6b7280',
      color: 'white',
      border: 'none',
      padding: '8px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    toolbar: {
      backgroundColor: 'white',
      padding: '12px 16px',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '20px'
    },
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flex: 1,
      overflow: 'hidden'
    },
    breadcrumbItem: {
      color: '#4f46e5',
      cursor: 'pointer',
      fontSize: '14px',
      whiteSpace: 'nowrap',
      textDecoration: 'none'
    },
    backNavigationButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '4px 8px',
      fontSize: '14px',
      color: '#6b7280',
      backgroundColor: '#f3f4f6',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginRight: '8px'
    },
    breadcrumbSeparator: {
      color: '#9ca3af',
      fontSize: '14px'
    },
    breadcrumbCurrent: {
      color: '#6b7280',
      fontSize: '14px',
      whiteSpace: 'nowrap'
    },
    controls: {
      display: 'flex',
      gap: '10px',
      alignItems: 'center'
    },
    viewToggle: {
      display: 'flex',
      borderRadius: '4px',
      overflow: 'hidden',
      border: '1px solid #e5e7eb'
    },
    viewButton: {
      padding: '6px 12px',
      border: 'none',
      background: 'white',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s'
    },
    viewButtonActive: {
      background: '#4f46e5',
      color: 'white'
    },
    sortDropdown: {
      padding: '6px 12px',
      border: '1px solid #e5e7eb',
      borderRadius: '4px',
      background: 'white',
      fontSize: '14px',
      cursor: 'pointer'
    },
    content: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      position: 'relative',
      minHeight: '400px'
    },
    selectionInfo: {
      position: 'absolute',
      top: '10px',
      right: '10px',
      background: '#4f46e5',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '4px',
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    clearSelectionButton: {
      background: 'none',
      border: 'none',
      color: 'white',
      cursor: 'pointer',
      padding: '2px 6px',
      borderRadius: '2px',
      fontSize: '12px'
    },
    gridView: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '16px',
      width: '100%'
    },
    listView: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px'
    },
    gridItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '16px',
      borderRadius: '8px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '2px solid transparent',
      userSelect: 'none',
      position: 'relative'
    },
    gridItemSelected: {
      backgroundColor: '#eff6ff',
      border: '2px solid #3b82f6'
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      border: '2px solid transparent',
      userSelect: 'none',
      position: 'relative'
    },
    listItemSelected: {
      backgroundColor: '#eff6ff',
      border: '2px solid #3b82f6'
    },
    itemIcon: {
      fontSize: '48px',
      marginBottom: '8px'
    },
    listItemIcon: {
      fontSize: '24px',
      marginRight: '12px'
    },
    itemName: {
      fontSize: '14px',
      textAlign: 'center',
      wordBreak: 'break-word',
      color: '#374151',
      marginTop: '8px',
      lineHeight: '1.4'
    },
    listItemName: {
      flex: 1,
      fontSize: '14px',
      color: '#374151'
    },
    listItemMeta: {
      display: 'flex',
      gap: '20px',
      fontSize: '13px',
      color: '#6b7280'
    },
    emptyState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6b7280'
    },
    loadingState: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#6b7280'
    },
    contextMenu: {
      position: 'fixed',
      backgroundColor: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      padding: '4px',
      minWidth: '180px',
      zIndex: 1000
    },
    contextMenuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      fontSize: '14px',
      cursor: 'pointer',
      borderRadius: '4px',
      transition: 'background 0.2s'
    },
    contextMenuItemHover: {
      backgroundColor: '#f3f4f6'
    },
    contextMenuDivider: {
      height: '1px',
      backgroundColor: '#e5e7eb',
      margin: '4px 0'
    },
    contextMenuItemDanger: {
      color: '#dc2626'
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>File Manager</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {uploadErrors.length > 0 && (
              <button
                onClick={() => setShowErrorReport(true)}
                style={{
                  ...styles.backButton,
                  backgroundColor: '#ef4444',
                  border: '1px solid #dc2626',
                  color: 'white',
                  fontSize: '14px',
                  padding: '8px 16px'
                }}
              >
                ⚠️ {uploadErrors.length} Upload Error{uploadErrors.length !== 1 ? 's' : ''}
              </button>
            )}
            <button onClick={onBack} style={styles.backButton}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <div style={styles.toolbar}>
        <div style={styles.breadcrumb}>
          {/* Back Button */}
          {currentFolder && (
            <button
              onClick={() => {
                if (breadcrumb.length > 1) {
                  // Navigate to parent folder
                  const parentFolder = breadcrumb[breadcrumb.length - 2];
                  navigateToFolder(parentFolder.id);
                } else {
                  // Navigate back to root
                  navigateToFolder(null);
                }
              }}
              style={styles.backNavigationButton}
              title="Go back"
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}
          
          <span
            style={styles.breadcrumbItem}
            onClick={() => navigateToFolder(null)}
          >
            Root
          </span>
          {breadcrumb.map((folder, index) => (
            <React.Fragment key={folder.id}>
              <span style={styles.breadcrumbSeparator}>/</span>
              {index === breadcrumb.length - 1 ? (
                <span style={styles.breadcrumbCurrent}>{folder.name}</span>
              ) : (
                <span
                  style={styles.breadcrumbItem}
                  onClick={() => navigateToFolder(folder.id)}
                >
                  {folder.name}
                </span>
              )}
            </React.Fragment>
          ))}
        </div>

        <div style={styles.controls}>
          <div style={styles.viewToggle}>
            <button
              style={{
                ...styles.viewButton,
                ...(viewMode === 'grid' ? styles.viewButtonActive : {})
              }}
              onClick={() => setViewMode('grid')}
            >
              Grid
            </button>
            <button
              style={{
                ...styles.viewButton,
                ...(viewMode === 'list' ? styles.viewButtonActive : {})
              }}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>

          <select
            style={styles.sortDropdown}
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
          >
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="date-asc">Date (Oldest)</option>
            <option value="date-desc">Date (Newest)</option>
            <option value="size-asc">Size (Smallest)</option>
            <option value="size-desc">Size (Largest)</option>
          </select>
        </div>
      </div>

      <div
        ref={contentRef}
        style={styles.content}
        onContextMenu={(e) => handleContextMenu(e)}
        onDragOver={handleFileDragOver}
        onDragEnter={handleFileDragEnter}
        onDragLeave={handleFileDragLeave}
        onDrop={handleFileDrop}
        onClick={(e) => {
          // Clear selection if clicking on empty space
          if (e.target === e.currentTarget) {
            setSelectedItems([]);
          }
        }}
      >
        {/* Selection info badge */}
        {selectedItems.length > 0 && (
          <div style={styles.selectionInfo}>
            <span>{selectedItems.length} selected</span>
            <button
              style={styles.clearSelectionButton}
              onClick={() => setSelectedItems([])}
              onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.2)'}
              onMouseOut={(e) => e.target.style.background = 'none'}
            >
              ✕
            </button>
          </div>
        )}
        {/* Drop overlay - only show for external file uploads, not internal drags */}
        {isDragging && currentFolder && !draggedItem && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(79, 70, 229, 0.1)',
            border: '2px dashed #4f46e5',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            pointerEvents: 'none'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px 40px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}>
              <p style={{ margin: 0, fontSize: '18px', color: '#4f46e5', fontWeight: '500' }}>
                Drop files or folders here to upload
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                Supports: PDF, DOC, DOCX, XLS, XLSX, CSV
              </p>
            </div>
          </div>
        )}
        {loading ? (
          <div style={styles.loadingState}>Loading...</div>
        ) : getSortedItems().length === 0 ? (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>
              This folder is empty
            </p>
            <p style={{ fontSize: '14px' }}>
              Right-click to create a new folder or upload files
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div style={styles.gridView}>
            {getSortedItems().map(item => {
              if (item.type === 'folder') {
                return (
                  <div
                    key={`folder-${item.id}`}
                    data-item-key={`folder-${item.id}`}
                    style={{
                      ...styles.gridItem,
                      ...(isItemSelected(item) ? styles.gridItemSelected : {}),
                      ...(dropTarget === item.id ? {
                        backgroundColor: '#dbeafe',
                        border: '2px solid #3b82f6'
                      } : {})
                    }}
                    onClick={(e) => handleItemClick(item, e)}
                    onDoubleClick={() => handleItemDoubleClick(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    onMouseOver={(e) => {
                      if (!isItemSelected(item) && dropTarget !== item.id) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isItemSelected(item) && dropTarget !== item.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                    onDragEnter={(e) => handleDragEnter(e, item)}
                    onDragLeave={(e) => handleDragLeave(e, item)}
                    onDrop={(e) => handleDrop(e, item)}
                  >
                    <div style={styles.itemIcon}>📁</div>
                    <div style={styles.itemName}>{item.name}</div>
                  </div>
                );
              } else {
                // Document - show as grid item similar to folder
                return (
                  <div
                    key={`document-${item.id}`}
                    data-item-key={`document-${item.id}`}
                    style={{
                      ...styles.gridItem,
                      ...(isItemSelected(item) ? styles.gridItemSelected : {})
                    }}
                    onClick={(e) => handleItemClick(item, e)}
                    onDoubleClick={() => handleItemDoubleClick(item)}
                    onContextMenu={(e) => handleContextMenu(e, item)}
                    onMouseOver={(e) => {
                      if (!isItemSelected(item)) {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isItemSelected(item)) {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, item)}
                    onDragEnd={handleDragEnd}
                  >
                    <div style={styles.itemIcon}>📄</div>
                    <div style={styles.itemName}>{item.name}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {formatFileSize(item.file_size)}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        ) : (
          <div style={styles.listView}>
            {getSortedItems().map(item => (
              <div
                key={`${item.type}-${item.id}`}
                data-item-key={`${item.type}-${item.id}`}
                style={{
                  ...styles.listItem,
                  ...(isItemSelected(item) ? styles.listItemSelected : {}),
                  ...(dropTarget === item.id && item.type === 'folder' ? {
                    backgroundColor: '#dbeafe',
                    border: '2px solid #3b82f6'
                  } : {})
                }}
                onClick={(e) => handleItemClick(item, e)}
                onDoubleClick={() => handleItemDoubleClick(item)}
                onContextMenu={(e) => handleContextMenu(e, item)}
                onMouseOver={(e) => {
                  if (!isItemSelected(item) && dropTarget !== item.id) {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }
                }}
                onMouseOut={(e) => {
                  if (!isItemSelected(item) && dropTarget !== item.id) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragEnter={(e) => item.type === 'folder' && handleDragEnter(e, item)}
                onDragLeave={(e) => item.type === 'folder' && handleDragLeave(e, item)}
                onDrop={(e) => item.type === 'folder' && handleDrop(e, item)}
              >
                <div style={styles.listItemIcon}>
                  {item.type === 'folder' ? '📁' : '📄'}
                </div>
                <div style={styles.listItemName}>{item.name}</div>
                <div style={styles.listItemMeta}>
                  {item.type === 'document' && (
                    <span>{formatFileSize(item.file_size)}</span>
                  )}
                  <span>{formatDate(item.created_at || item.uploaded_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          style={{
            ...styles.contextMenu,
            left: contextMenu.x,
            top: contextMenu.y
          }}
        >
          {contextMenu.items.map((item, index) => (
            item.divider ? (
              <div key={index} style={styles.contextMenuDivider} />
            ) : (
              <div
                key={index}
                style={{
                  ...styles.contextMenuItem,
                  ...(item.danger ? styles.contextMenuItemDanger : {})
                }}
                onClick={() => {
                  item.action();
                  setContextMenu(null);
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </div>
            )
          ))}
        </div>
      )}

      {/* Modals */}
      {showNewFolderModal && (
        <NewFolderModal
          parentId={currentFolder}
          onClose={() => setShowNewFolderModal(false)}
          onSuccess={() => {
            setShowNewFolderModal(false);
            loadFolderContents(currentFolder);
            showToast('Folder created successfully');
          }}
        />
      )}

      {showRenameModal && targetItem && (
        <RenameModal
          item={targetItem}
          onClose={() => {
            setShowRenameModal(false);
            setTargetItem(null);
          }}
          onSuccess={() => {
            setShowRenameModal(false);
            setTargetItem(null);
            loadFolderContents(currentFolder);
            showToast('Item renamed successfully');
          }}
        />
      )}

      {showPermissionsModal && targetItem && (
        <PermissionsModal
          folder={targetItem}
          onClose={() => {
            setShowPermissionsModal(false);
            setTargetItem(null);
          }}
          onSuccess={() => {
            setShowPermissionsModal(false);
            setTargetItem(null);
            showToast('Permissions updated successfully');
          }}
        />
      )}

      {showMoveModal && targetItem && (
        <MoveModal
          item={targetItem}
          currentFolderId={currentFolder}
          onClose={() => {
            setShowMoveModal(false);
            setTargetItem(null);
          }}
          onSuccess={() => {
            setShowMoveModal(false);
            setTargetItem(null);
            loadFolderContents(currentFolder);
            showToast('Item moved successfully');
          }}
        />
      )}

      {showErrorReport && (
        <ErrorReportModal
          errors={uploadErrors}
          onClose={() => setShowErrorReport(false)}
          onRetry={retryFailedUploads}
          onClearErrors={() => setUploadErrors([])}
        />
      )}

      {showPasteConfirmModal && copiedFolder && (
        <PasteConfirmModal
          folderName={copiedFolder.name}
          onClose={() => setShowPasteConfirmModal(false)}
          onConfirm={(newName, skipRoot) => handlePasteFoldersOnly(newName, skipRoot)}
        />
      )}
    </div>
  );
};

// Modal Components
const NewFolderModal = ({ parentId, onClose, onSuccess }) => {
  const [folderName, setFolderName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: folderName,
          parent_id: parentId
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to create folder');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '400px',
      maxWidth: '90%'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '14px',
      marginBottom: '16px'
    },
    error: {
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '16px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    primaryButton: {
      backgroundColor: '#4f46e5',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>Create New Folder</h3>
        <input
          type="text"
          placeholder="Folder name"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
          style={modalStyles.input}
          autoFocus
        />
        {error && <div style={modalStyles.error}>{error}</div>}
        <div style={modalStyles.buttonGroup}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.secondaryButton }}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            style={{ ...modalStyles.button, ...modalStyles.primaryButton }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

const RenameModal = ({ item, onClose, onSuccess }) => {
  const [newName, setNewName] = useState(item.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!newName.trim()) {
      setError('Name is required');
      return;
    }

    if (newName === item.name) {
      onClose();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const endpoint = item.type === 'folder'
        ? `/admin/folders/${item.id}`
        : `/admin/documents/${item.id}`;

      const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newName,
          ...(item.type === 'folder' ? { parent_id: item.parent_id } : {})
        })
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to rename');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '400px',
      maxWidth: '90%'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px'
    },
    input: {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '14px',
      marginBottom: '16px'
    },
    error: {
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '16px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    primaryButton: {
      backgroundColor: '#4f46e5',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>Rename {item.type === 'folder' ? 'Folder' : 'Document'}</h3>
        <input
          type="text"
          placeholder="New name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
          style={modalStyles.input}
          autoFocus
        />
        {error && <div style={modalStyles.error}>{error}</div>}
        <div style={modalStyles.buttonGroup}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.secondaryButton }}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            style={{ ...modalStyles.button, ...modalStyles.primaryButton }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Renaming...' : 'Rename'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PermissionsModal = ({ folder, onClose, onSuccess }) => {
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/groups`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch groups');
      }
      
      const data = await response.json();
      setGroups(data);

      // Find currently selected groups for this specific folder
      // Convert folder.id to number to ensure proper comparison
      const folderIdNumber = parseInt(folder.id, 10);
      
      const selected = data
        .filter(group => {
          // Check if this group has access to the current folder
          // Ensure both values are numbers for comparison
          return group.group_folders && group.group_folders.some(folderId => 
            parseInt(folderId, 10) === folderIdNumber
          );
        })
        .map(group => group.id);
      
      console.log('Folder ID:', folder.id, '(as number:', folderIdNumber, ')');
      console.log('Groups data:', data);
      console.log('Currently selected groups:', selected);
      
      setSelectedGroups(selected);
      
    } catch (err) {
      console.error('Error fetching groups and permissions:', err);
      setError('Failed to load groups and permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');

    try {
      // Convert folder.id to number for consistent comparison
      const folderIdNumber = parseInt(folder.id, 10);
      
      // Update each group's folder permissions
      const updatePromises = groups.map(async (group) => {
        const shouldHaveAccess = selectedGroups.includes(group.id);
        const currentlyHasAccess = group.group_folders && group.group_folders.some(folderId => 
          parseInt(folderId, 10) === folderIdNumber
        );

        // Only update if there's a change
        if (shouldHaveAccess !== currentlyHasAccess) {
          let newFolders;
          
          if (shouldHaveAccess) {
            // Add this folder to the group's accessible folders
            newFolders = [...(group.group_folders || []), folderIdNumber];
          } else {
            // Remove this folder from the group's accessible folders
            newFolders = (group.group_folders || []).filter(folderId => 
              parseInt(folderId, 10) !== folderIdNumber
            );
          }

          console.log(`Updating group ${group.name}: ${currentlyHasAccess ? 'removing' : 'adding'} access to folder ${folderIdNumber}`);

          const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/groups/${group.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              name: group.name,
              description: group.description,
              folders: newFolders
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Failed to update group ${group.name}: ${errorData.message || 'Unknown error'}`);
          }
          
          return response;
        }
        
        return Promise.resolve(); // No change needed
      });

      await Promise.all(updatePromises);
      onSuccess();
      
    } catch (err) {
      console.error('Error updating permissions:', err);
      setError(err.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '500px',
      maxWidth: '90%',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '16px'
    },
    groupList: {
      flex: 1,
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '16px'
    },
    groupItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px',
      borderRadius: '4px',
      marginBottom: '8px',
      cursor: 'pointer',
      transition: 'background 0.2s'
    },
    checkbox: {
      marginRight: '12px'
    },
    groupName: {
      flex: 1,
      fontSize: '14px'
    },
    groupDesc: {
      fontSize: '12px',
      color: '#6b7280',
      marginTop: '2px'
    },
    error: {
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '16px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    primaryButton: {
      backgroundColor: '#4f46e5',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>Edit Permissions - {folder.name}</h3>
        <p style={modalStyles.subtitle}>
          Select which groups can access this folder and all its contents
        </p>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Loading groups...</div>
        ) : (
          <div style={modalStyles.groupList}>
            {groups.map(group => (
              <div
                key={group.id}
                style={modalStyles.groupItem}
                onClick={() => {
                  setSelectedGroups(prev =>
                    prev.includes(group.id)
                      ? prev.filter(g => g !== group.id)
                      : [...prev, group.id]
                  );
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedGroups.includes(group.id)}
                  onChange={() => { }}
                  style={modalStyles.checkbox}
                />
                <div>
                  <div style={modalStyles.groupName}>{group.name}</div>
                  {group.description && (
                    <div style={modalStyles.groupDesc}>{group.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <div style={modalStyles.error}>{error}</div>}

        <div style={modalStyles.buttonGroup}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.secondaryButton }}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            style={{ ...modalStyles.button, ...modalStyles.primaryButton }}
            onClick={handleSubmit}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MoveModal = ({ item, currentFolderId, onClose, onSuccess }) => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFolders();
  }, []);

  const fetchFolders = async () => {
    try {
      const response = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Filter out items that shouldn't be available as targets
        const filtered = data.filter(f => {
          if (item.type === 'folder') {
            // Can't move a folder into itself or its children
            return f.id !== item.id && !f.path.startsWith(item.path + '/');
          } else if (item.type === 'multiple') {
            // For multiple selections, filter out any selected folders and their children
            const selectedFolderIds = item.selectedItems
              .filter(key => key.startsWith('folder-'))
              .map(key => parseInt(key.split('-')[1]));
            
            if (selectedFolderIds.includes(f.id)) {
              return false;
            }
            
            // Also check if this folder is a child of any selected folder
            return !selectedFolderIds.some(selectedId => {
              const selectedFolder = data.find(folder => folder.id === selectedId);
              return selectedFolder && f.path.startsWith(selectedFolder.path + '/');
            });
          }
          return true;
        });
        
        setFolders(buildFolderTree(filtered));
      }
    } catch (err) {
      setError('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const buildFolderTree = (folders, parentId = null) => {
    return folders
      .filter(f => f.parent_id === parentId)
      .map(f => ({
        ...f,
        children: buildFolderTree(folders, f.id)
      }));
  };

  const handleSubmit = async () => {
    if (selectedFolder === currentFolderId) {
      setError('Item is already in this folder');
      return;
    }

    setMoving(true);
    setError('');

    try {
      if (item.type === 'multiple') {
        // Handle multiple items
        let movedCount = 0;
        let errorCount = 0;
        
        for (const itemKey of item.selectedItems) {
          const [type, id] = itemKey.split('-');
          
          try {
            const endpoint = type === 'folder'
              ? `/admin/folders/${id}`
              : `/admin/documents/${id}/move`;

            const body = type === 'folder'
              ? { parent_id: selectedFolder }
              : { folder_id: selectedFolder };

            // For folders, we need to get the current name
            if (type === 'folder') {
              const folderResponse = await fetch(`${window.rwcvaultData.apiUrl}/admin/folders`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`
                }
              });
              
              if (folderResponse.ok) {
                const allFolders = await folderResponse.json();
                const folder = allFolders.find(f => f.id === parseInt(id));
                if (folder) {
                  body.name = folder.name;
                }
              }
            }

            const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(body)
            });

            if (response.ok) {
              movedCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }
        
        if (movedCount > 0 && errorCount === 0) {
          onSuccess();
        } else if (movedCount > 0) {
          setError(`Moved ${movedCount} items, ${errorCount} failed`);
        } else {
          setError(`Failed to move ${errorCount} items`);
        }
      } else {
        // Handle single item
        const endpoint = item.type === 'folder'
          ? `/admin/folders/${item.id}`
          : `/admin/documents/${item.id}/move`;

        const body = item.type === 'folder'
          ? { name: item.name, parent_id: selectedFolder }
          : { folder_id: selectedFolder };

        const response = await fetch(`${window.rwcvaultData.apiUrl}${endpoint}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('rwcvault_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });

        if (response.ok) {
          onSuccess();
        } else {
          const data = await response.json();
          setError(data.message || 'Failed to move item');
        }
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setMoving(false);
    }
  };

  const renderFolderTree = (folders, level = 0) => {
    return folders.map(folder => (
      <div key={folder.id}>
        <div
          style={{
            padding: '8px',
            paddingLeft: `${12 + level * 20}px`,
            cursor: 'pointer',
            borderRadius: '4px',
            backgroundColor: selectedFolder === folder.id ? '#eff6ff' : 'transparent',
            transition: 'background 0.2s'
          }}
          onClick={() => setSelectedFolder(folder.id)}
          onMouseOver={(e) => {
            if (selectedFolder !== folder.id) {
              e.currentTarget.style.backgroundColor = '#f9fafb';
            }
          }}
          onMouseOut={(e) => {
            if (selectedFolder !== folder.id) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          📁 {folder.name}
        </div>
        {folder.children && folder.children.length > 0 && (
          renderFolderTree(folder.children, level + 1)
        )}
      </div>
    ));
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '500px',
      maxWidth: '90%',
      maxHeight: '80vh',
      display: 'flex',
      flexDirection: 'column'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '16px'
    },
    folderList: {
      flex: 1,
      overflowY: 'auto',
      border: '1px solid #e5e7eb',
      borderRadius: '4px',
      padding: '12px',
      marginBottom: '16px'
    },
    rootOption: {
      padding: '8px 12px',
      cursor: 'pointer',
      borderRadius: '4px',
      backgroundColor: selectedFolder === null ? '#eff6ff' : 'transparent',
      transition: 'background 0.2s',
      marginBottom: '8px'
    },
    error: {
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '16px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '10px',
      justifyContent: 'flex-end'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      fontSize: '14px',
      cursor: 'pointer'
    },
    primaryButton: {
      backgroundColor: '#4f46e5',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#e5e7eb',
      color: '#374151'
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>Move "{item.name}"</h3>
        <p style={modalStyles.subtitle}>
          Select the destination folder
        </p>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Loading folders...</div>
        ) : (
          <div style={modalStyles.folderList}>
            <div
              style={modalStyles.rootOption}
              onClick={() => setSelectedFolder(null)}
              onMouseOver={(e) => {
                if (selectedFolder !== null) {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }
              }}
              onMouseOut={(e) => {
                if (selectedFolder !== null) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              📁 Root (Top Level)
            </div>
            {renderFolderTree(folders)}
          </div>
        )}

        {error && <div style={modalStyles.error}>{error}</div>}

        <div style={modalStyles.buttonGroup}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.secondaryButton }}
            onClick={onClose}
            disabled={moving}
          >
            Cancel
          </button>
          <button
            style={{ ...modalStyles.button, ...modalStyles.primaryButton }}
            onClick={handleSubmit}
            disabled={moving || loading}
          >
            {moving ? 'Moving...' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Error Report Modal Component
const ErrorReportModal = ({ errors, onClose, onRetry, onClearErrors }) => {
  const [selectedErrors, setSelectedErrors] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    // Update selectAll state based on selection
    setSelectAll(selectedErrors.length === errors.length && errors.length > 0);
  }, [selectedErrors, errors]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedErrors([]);
    } else {
      setSelectedErrors([...errors]);
    }
  };

  const handleSelectError = (error) => {
    setSelectedErrors(prev => {
      const isSelected = prev.some(e => e.fileName === error.fileName && e.timestamp === error.timestamp);
      if (isSelected) {
        return prev.filter(e => !(e.fileName === error.fileName && e.timestamp === error.timestamp));
      } else {
        return [...prev, error];
      }
    });
  };

  const handleRetrySelected = async () => {
    if (selectedErrors.length === 0) return;
    
    await onRetry(selectedErrors);
    setSelectedErrors([]);
  };

  const handleRetryAll = async () => {
    await onRetry(errors);
    setSelectedErrors([]);
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      maxWidth: '800px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    title: {
      fontSize: '20px',
      fontWeight: 'bold',
      marginBottom: '8px',
      color: '#dc2626'
    },
    subtitle: {
      fontSize: '14px',
      color: '#6b7280',
      marginBottom: '20px'
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    errorList: {
      marginBottom: '20px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    errorItem: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '12px',
      borderBottom: '1px solid #f3f4f6',
      backgroundColor: '#fef2f2'
    },
    errorItemSelected: {
      backgroundColor: '#dbeafe',
      borderColor: '#3b82f6'
    },
    checkbox: {
      marginRight: '12px',
      marginTop: '2px'
    },
    errorDetails: {
      flex: 1,
      minWidth: 0
    },
    fileName: {
      fontWeight: '600',
      color: '#111827',
      wordBreak: 'break-word',
      marginBottom: '4px'
    },
    errorMessage: {
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '4px'
    },
    timestamp: {
      color: '#6b7280',
      fontSize: '12px'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      fontSize: '14px'
    },
    primaryButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    },
    secondaryButton: {
      backgroundColor: '#6b7280',
      color: 'white'
    },
    dangerButton: {
      backgroundColor: '#ef4444',
      color: 'white'
    },
    successButton: {
      backgroundColor: '#10b981',
      color: 'white'
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={modalStyles.title}>⚠️ Upload Error Report</h3>
        <p style={modalStyles.subtitle}>
          {errors.length} file{errors.length !== 1 ? 's' : ''} failed to upload. 
          Select individual files or use "Retry All" to retry all failed uploads.
        </p>

        <div style={modalStyles.controls}>
          <div style={modalStyles.checkboxContainer}>
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              style={modalStyles.checkbox}
            />
            <label>Select All ({errors.length} files)</label>
          </div>
          
          <div style={modalStyles.buttonGroup}>
            <button
              style={{ ...modalStyles.button, ...modalStyles.successButton }}
              onClick={handleRetrySelected}
              disabled={selectedErrors.length === 0}
            >
              Retry Selected ({selectedErrors.length})
            </button>
            <button
              style={{ ...modalStyles.button, ...modalStyles.successButton }}
              onClick={handleRetryAll}
            >
              Retry All
            </button>
            <button
              style={{ ...modalStyles.button, ...modalStyles.dangerButton }}
              onClick={() => {
                onClearErrors();
                onClose();
              }}
            >
              Clear All Errors
            </button>
          </div>
        </div>

        <div style={modalStyles.errorList}>
          {errors.map((error, index) => {
            const isSelected = selectedErrors.some(e => 
              e.fileName === error.fileName && e.timestamp === error.timestamp
            );
            
            return (
              <div
                key={`${error.fileName}-${error.timestamp}`}
                style={{
                  ...modalStyles.errorItem,
                  ...(isSelected ? modalStyles.errorItemSelected : {}),
                  ...(index === errors.length - 1 ? { borderBottom: 'none' } : {})
                }}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleSelectError(error)}
                  style={modalStyles.checkbox}
                />
                <div style={modalStyles.errorDetails}>
                  <div style={modalStyles.fileName}>{error.fileName}</div>
                  <div style={modalStyles.errorMessage}>Error: {error.error}</div>
                  <div style={modalStyles.timestamp}>
                    Failed at: {formatTimestamp(error.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={modalStyles.buttonGroup}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.secondaryButton }}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Paste Confirmation Modal
const PasteConfirmModal = ({ folderName, onClose, onConfirm }) => {
  const [newFolderName, setNewFolderName] = useState(folderName);
  const [skipRoot, setSkipRoot] = useState(false);

  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000
    },
    modal: {
      background: 'white',
      borderRadius: '8px',
      padding: '24px',
      width: '450px',
      maxWidth: '90%'
    },
    title: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '16px',
      color: '#111827'
    },
    message: {
      fontSize: '14px',
      color: '#4b5563',
      marginBottom: '16px',
      lineHeight: '1.5'
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '6px',
      display: 'block'
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      fontSize: '14px',
      marginBottom: '16px',
      boxSizing: 'border-box'
    },
    note: {
      fontSize: '12px',
      color: '#6b7280',
      marginBottom: '20px'
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px'
    },
    button: {
      padding: '8px 16px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '14px'
    },
    cancelButton: {
      backgroundColor: '#f3f4f6',
      color: '#374151'
    },
    confirmButton: {
      backgroundColor: '#3b82f6',
      color: 'white'
    }
  };

  const handleSubmit = () => {
    if (!skipRoot && !newFolderName.trim()) {
      return;
    }
    onConfirm(skipRoot ? null : newFolderName.trim(), skipRoot);
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={e => e.stopPropagation()}>
        <div style={modalStyles.title}>Paste Folder Structure</div>
        <div style={modalStyles.message}>
          {skipRoot
            ? 'You are pasting only the sub-folders directly into this location. The root folder will not be created. Documents will not be copied.'
            : 'You are pasting the folder structure and all sub-folders. Documents will not be copied.'}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
          <input
            type="checkbox"
            checked={skipRoot}
            onChange={(e) => setSkipRoot(e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          Don't include Root Folder
        </label>
        {!skipRoot && (
          <>
            <label style={modalStyles.label}>Folder Name</label>
            <input
              type="text"
              style={modalStyles.input}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              autoFocus
            />
            <div style={modalStyles.note}>
              You can rename the folder here (e.g., change "October" to "December")
            </div>
          </>
        )}
        <div style={modalStyles.buttonGroup}>
          <button
            style={{ ...modalStyles.button, ...modalStyles.cancelButton }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            style={{ ...modalStyles.button, ...modalStyles.confirmButton }}
            onClick={handleSubmit}
            disabled={!skipRoot && !newFolderName.trim()}
          >
            Paste Folders
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileManager;